import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMqttProxy } from '../mqttProxy';
import { aesEncrypt, aesDecrypt, stringToHex, hexToString, generateK1 } from '../cryptoUtils';
import { sendTamAck } from '../tamAck';

const API_BASE = '';  // Relative URL

const RAZORPAY_KEY = 'rzp_live_Sv53lVIUyJGV0d';

// Same Lambda/API Gateway as tickets — plan pricing is routed via ?resource=plan
const TICKETS_API   = 'https://g3mu2eke0l.execute-api.ap-south-1.amazonaws.com/dev_waas_customersupport/ticket';
const PLAN_API_BASE = `${TICKETS_API}?resource=plan`;

// ── MQTT topics ─────────────────────────────────────────────────────────────────
const sanitiseMac = (mac) => {
  const m = (mac || '').replace(/[^a-fA-F0-9]/g, '');
  if (m.length === 13) return (m.slice(0, 7) + m.slice(8)).toLowerCase();
  return m.toLowerCase();
};
const CMD_TOPIC  = (mac) => `/hoags/livpure/lotier/waas/${sanitiseMac(mac)}/`;
const RESP_TOPIC = (mac) => `/hoags/livpure/lotier/waas/response/${sanitiseMac(mac)}/`;
const DS_TIMEOUT = 25000;
const LAST_STATUS_API = '/api/device-status';
const nowStr = () => new Date().toLocaleTimeString('en-GB', { hour12: false });

// ── Unique 5-char hex transaction ID ─────────────────────────────────
const genTxnId = () => Math.floor(Math.random()*0xFFFFF).toString(16).toUpperCase().padStart(5,'0');

// Current timestamp in DDMMYY HH:MM:SS
const nowDDMMYY = () => {
  const d = new Date();
  const dd   = String(d.getDate()).padStart(2,'0');
  const mm   = String(d.getMonth()+1).padStart(2,'0');
  const yy   = String(d.getFullYear()).slice(-2);
  const hh   = String(d.getHours()).padStart(2,'0');
  const min  = String(d.getMinutes()).padStart(2,'0');
  const ss   = String(d.getSeconds()).padStart(2,'0');
  return { date:`${dd}${mm}${yy}`, ts:`${dd}${mm}${yy} ${hh}:${min}:${ss}` };
};

// Compute validity date DDMMYY from today + days
const validityDate = (days) => {
  const d = new Date(); d.setDate(d.getDate() + days);
  const dd = String(d.getDate()).padStart(2,'0');
  const mm = String(d.getMonth()+1).padStart(2,'0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}${mm}${yy}`;
};

// ── Plan config — single source of truth for ALL plan durations ─────────────
// When API adds a new duration (e.g. price_2m), just add one line here.
// Everything else (card rendering, Razorpay, MQTT payload) reads from this.
const PLAN_CONFIG = {
  '1m':  { label: '1 Month',   days: 30,   litres: '10',  color: '#FF9F0A', accent: '#CC7A00' },
  '3m':  { label: '3 Months',  days: 90,   litres: '30',  color: '#007AFF', accent: '#0056d3' },
  '6m':  { label: '6 Months',  days: 180,  litres: '60',  color: '#AF52DE', accent: '#8B36B0', badge: 'Popular' },
  '12m': { label: '1 Year',    days: 365,  litres: '100', color: '#34C759', accent: '#248A3D', badge: 'Best Value' },
};

// ── Hardcoded fallback plans (shown when API has no custom pricing) ──────────
const FALLBACK_PLANS = [
  { id:'3m',  label:'3 Months', price:'₹299', _priceNum:299, litres:'30',  days:90,  color:'#007AFF', accent:'#0056d3', link:'' },
  { id:'6m',  label:'6 Months', price:'₹599', _priceNum:599, litres:'60',  days:180, color:'#AF52DE', accent:'#8B36B0', badge:'Popular', link:'' },
  { id:'12m', label:'1 Year',   price:'₹999', _priceNum:999, litres:'100', days:365, color:'#34C759', accent:'#248A3D', badge:'Best Value', link:'' },
];

// ── Dynamically build plans from API response ───────────────────────────────
// Reads every `price_*` key from the API, maps to PLAN_CONFIG for metadata,
// and returns a sorted array. If API adds price_2m tomorrow, just add '2m'
// to PLAN_CONFIG and the frontend picks it up automatically.
function buildPlansFromApi(planRow) {
  if (!planRow) return [];

  return Object.keys(planRow)
    .filter(key => key.startsWith('price_') && planRow[key] != null)
    .map(key => {
      const duration = key.replace('price_', ''); // "1m", "3m", "6m", "12m"
      const config = PLAN_CONFIG[duration];
      if (!config) {
        console.warn(`[Plans] Unknown duration "${duration}" from API key "${key}" — skipping. Add it to PLAN_CONFIG.`);
        return null;
      }

      return {
        id:       duration,
        label:    config.label,
        price:    `₹${planRow[key]}`,
        _priceNum: Number(planRow[key]),
        litres:   config.litres,
        days:     config.days,
        color:    config.color,
        accent:   config.accent,
        badge:    config.badge || null,
        link:     '',
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.days - b.days); // shortest → longest
}

// ── Recharge Sheet with RC response listening ─────────────────────
function RechargeSheet({ device, mac, publish, subscribe, onClose, k2 }) {
  const [activePlan, setActivePlan] = useState(null);
  const [rcLog,      setRcLog]      = useState([]);
  const [rcResp,     setRcResp]     = useState(null);
  const [showSuccess,setShowSuccess]= useState(false);
  const [customPricing,  setCustomPricing]  = useState(null);
  const [pricingChecked, setPricingChecked] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);

  // ── Look up custom plan pricing for this device's model + region ──────
  useEffect(() => {
    // const modelNo = device?._registration?.modelNo;
    // const region  = device?._registration?.location;
    const modelNo = "0040011LTRD1";
    const region = "Tamil Nadu";
    if (!modelNo || !region) { setPricingChecked(true); return; }
    setPricingLoading(true);
    setPricingChecked(false);
    const url = `${PLAN_API_BASE}&model_no=${encodeURIComponent(modelNo)}&region=${encodeURIComponent(region.trim())}`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        const plan = data.plan;
        setCustomPricing(plan && plan.model_no ? plan : null);
      })
      .catch(() => setCustomPricing(null))
      .finally(() => { setPricingLoading(false); setPricingChecked(true); });
  }, [device]); // eslint-disable-line

  // ── Build display plans: dynamic from API, fallback to hardcoded ──────
  const displayPlans = customPricing
    ? buildPlansFromApi(customPricing)
    : FALLBACK_PLANS;

  console.log("customPricing:", customPricing);
  console.log("displayPlans:", displayPlans);

  const handlePay = (plan) => {
    const txn      = genTxnId();
    // plan.litres and plan.days come from the plan object itself — no separate lookup needed
    const litres   = plan.litres || '30';
    const validity = validityDate(plan.days || 90);
    const payload  = { IM: mac.toLowerCase(), CMD:'RC', L:litres, V:validity, CF:'Y', X:txn, OW:'N' };
    const topic    = CMD_TOPIC(mac);
    const respTop  = RESP_TOPIC(mac);

    setActivePlan(plan);
    setRcLog([]);
    setRcResp(null);
    setShowSuccess(false);

    // Subscribe to MQTT response BEFORE opening payment
    const unsub = subscribe(respTop, async (msg) => {
      try {
        let json;
        const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
        if (parsed.VAL && /^[0-9a-fA-F]+$/.test(parsed.VAL) && k2) {
          const decHex = await aesDecrypt(parsed.VAL, k2);
          json = JSON.parse(hexToString(decHex));
        } else {
          json = parsed;
        }
        if (json.CMD === 'RC') {
          setRcResp(json);
          setShowSuccess(true);
          const txHistory = JSON.parse(localStorage.getItem('hoags_txn_history')||'[]');
          txHistory.unshift({ type:'RC', device:device.friendlyname, mac, plan:plan.label, price:plan.price, payload, response:json, time:new Date().toISOString() });
          localStorage.setItem('hoags_txn_history', JSON.stringify(txHistory.slice(0,100)));
          setTimeout(() => unsub(), 100);
        }
      } catch(e) { console.error('[RC] response error:', e); }
    });

    // Send MQTT RC command — accepts optional payload override (for payment ID txn)
    const sendMqttCommand = async (overridePayload) => {
      const finalPayload = overridePayload || payload;
      if (k2) {
        try {
          const plainHex  = stringToHex(JSON.stringify(finalPayload));
          const encrypted = await aesEncrypt(plainHex, k2);
          publish(topic, encrypted);
        } catch(e) {
          console.error('[RC] encrypt error, fallback plaintext:', e);
          publish(topic, JSON.stringify(finalPayload));
        }
      } else {
        publish(topic, JSON.stringify(finalPayload));
      }
    };

    // Load Razorpay SDK dynamically
    const loadRazorpay = () => new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload  = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

    loadRazorpay().then((loaded) => {
      if (!loaded) {
        window.open(plan.link, '_blank', 'noopener,noreferrer');
        setTimeout(sendMqttCommand, 5000);
        return;
      }

      // Use _priceNum from the plan object (already in rupees) → convert to paise
      const amountPaise = plan._priceNum != null
        ? Math.round(plan._priceNum * 100)
        : 29900; // fallback ₹299

      const options = {
        key:         RAZORPAY_KEY,
        amount:      amountPaise,
        currency:    'INR',
        name:        'Havells Water Purifier',
        description: `${plan.label} Subscription — ${device.friendlyname}`,
        image:       '/havells-logo.png',
        notes: {
          device_mac:  mac,
          device_name: device.friendlyname,
          plan:        plan.label,
          litres,
          validity,
          txn_id:      txn,
        },
        theme: { color: '#DC2626' },
        handler: function(response) {
          const rzpId = response.razorpay_payment_id || '';
          const txnFromPayment = rzpId.slice(-4).toUpperCase() || txn;
          const finalPayload = { ...payload, X: txnFromPayment };
          console.log(`[RC] Payment ID: ${rzpId}, TXN: ${txnFromPayment}`);
          sendMqttCommand(finalPayload);
        },
        modal: {
          ondismiss: function() {
            unsub();
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function(resp) {
        console.error('[Razorpay] payment failed:', resp.error);
        unsub();
      });
      rzp.open();
    });
  };

  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, zIndex:9999,
      background:'rgba(0,0,0,0.55)', backdropFilter:'blur(16px)',
      display:'flex', alignItems:'flex-end', justifyContent:'center',
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'#fff', borderRadius:'24px 24px 0 0',
        padding:'24px 20px 36px', width:'100%', maxWidth:520,
        boxShadow:'0 -20px 60px rgba(0,0,0,0.25)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",
        maxHeight:'90vh', overflowY:'auto',
      }}>
        <div style={{ width:36, height:4, borderRadius:2, background:'#e5e5ea', margin:'0 auto 18px' }}/>

        {/* iOS success popup */}
        {showSuccess && rcResp && (
          <div style={{
            position:'fixed', inset:0, zIndex:99999,
            display:'flex', alignItems:'center', justifyContent:'center',
            background:'rgba(0,0,0,0.4)', backdropFilter:'blur(8px)',
            animation:'fadeUp .2s ease',
          }}>
            <div style={{
              background:'#fff', borderRadius:28,
              padding:'36px 32px 28px', textAlign:'center',
              width:290, boxShadow:'0 24px 80px rgba(0,0,0,0.3)',
              animation:'fadeUp .3s cubic-bezier(.4,0,.2,1)',
            }}>
              <div style={{
                width:72, height:72, borderRadius:'50%', margin:'0 auto 18px',
                background:'linear-gradient(135deg,#34C759,#248A3D)',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 8px 32px rgba(52,199,89,.45)',
              }}>
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12l5 5L19 7" stroke="#fff" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={{ fontSize:18, fontWeight:800, color:'#1c1c1e', marginBottom:6 }}>
                Payment Successful!
              </div>
              <div style={{ fontSize:13, color:'#8E8E93', lineHeight:1.7, marginBottom:20 }}>
                {rcResp.NL != null && <div>💧 New Litres: <b style={{color:'#1c1c1e'}}>{rcResp.NL}</b></div>}
                {rcResp.V  != null && <div>📅 Validity Upto: <b style={{color:'#1c1c1e'}}>{rcResp.V}</b></div>}
                {rcResp.X  != null && <div style={{fontSize:11,color:'#C7C7CC',marginTop:6}}>Txn: {rcResp.X}</div>}
              </div>
              <button onClick={()=>{setShowSuccess(false);onClose();}} style={{
                width:'100%', padding:'12px', borderRadius:14, border:'none',
                background:'linear-gradient(135deg,#34C759,#248A3D)',
                color:'#fff', fontWeight:700, fontSize:15,
                cursor:'pointer', fontFamily:'inherit',
                boxShadow:'0 4px 14px rgba(52,199,89,.4)',
              }}>Done</button>
            </div>
          </div>
        )}

        <div style={{ fontSize:18, fontWeight:800, color:'#1c1c1e', marginBottom:3 }}>Recharge Plan</div>
        <div style={{ fontSize:12, color:'#8E8E93', marginBottom:12 }}>
          {device.friendlyname} · {mac}
        </div>

        {/* Pricing lookup banner */}
        {pricingLoading ? (
          <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:11, background:'rgba(0,0,0,.03)',
            border:'1px solid rgba(0,0,0,.06)', display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:13, height:13, borderRadius:'50%', border:'2px solid rgba(0,122,255,.3)',
              borderTopColor:'#007AFF', animation:'spinAnim .7s linear infinite' }}/>
            <span style={{ fontSize:11, color:'#8E8E93' }}>Checking custom pricing for this model & region…</span>
          </div>
        ) : pricingChecked && customPricing ? (
          <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:11, background:'rgba(255,159,10,.08)',
            border:'1px solid rgba(255,159,10,.2)', display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:14 }}>🏷️</span>
            <span style={{ fontSize:11, color:'#E6870A', fontWeight:600 }}>
              Custom pricing for {customPricing.model_no} · {customPricing.region}
            </span>
          </div>
        ) : pricingChecked ? (
          <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:11, background:'rgba(0,0,0,.03)',
            border:'1px solid rgba(0,0,0,.06)', display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:13 }}>ℹ️</span>
            <span style={{ fontSize:11, color:'#8E8E93' }}>
              No custom pricing for this device — showing default prices
            </span>
          </div>
        ) : null}

        <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:16 }}>
          {displayPlans.map(plan => (
            <div key={plan.id} style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'14px 16px', borderRadius:16,
              border:`1.5px solid ${plan.color}22`, background:`${plan.color}06`,
              position:'relative', overflow:'hidden',
            }}>
              <div style={{ position:'absolute',left:0,top:0,bottom:0,width:4,
                background:`linear-gradient(180deg,${plan.color},${plan.accent})`,
                borderRadius:'4px 0 0 4px' }}/>
              <div style={{ paddingLeft:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                  <span style={{ fontSize:15, fontWeight:700, color:'#1c1c1e' }}>{plan.label}</span>
                  {plan.badge && (
                    <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:20,
                      background:`linear-gradient(135deg,${plan.color},${plan.accent})`,
                      color:'#fff', letterSpacing:.4 }}>{plan.badge}</span>
                  )}
                </div>
                <div style={{ fontSize:11, color:'#8E8E93', marginBottom:4 }}>
                  {plan.litres}L water · {plan.days} days
                </div>
                <div style={{ fontSize:22, fontWeight:900, color:plan.color, letterSpacing:-.4 }}>
                  {plan.price}
                </div>
              </div>
              <button onClick={() => handlePay(plan)} style={{
                padding:'10px 20px', borderRadius:12, border:'none',
                background:`linear-gradient(135deg,${plan.color},${plan.accent})`,
                color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer',
                fontFamily:'inherit', boxShadow:`0 4px 14px ${plan.color}40`,
              }}>Pay</button>
            </div>
          ))}
        </div>

        <div style={{ fontSize:10, color:'#C7C7CC', textAlign:'center', marginBottom:14 }}>
          RC command auto-sent 5s after payment
        </div>
        <button onClick={onClose} style={{
          width:'100%', padding:'14px', borderRadius:14,
          border:'1.5px solid rgba(0,0,0,.1)', background:'#fff',
          color:'#8E8E93', fontWeight:600, fontSize:14,
          cursor:'pointer', fontFamily:'inherit',
        }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Send Request Sheet — AL, EV, RL, RV only ─────────────────────
function SendRequestSheet({ device, mac, connected, publish, subscribe, onClose, k2 }) {
  const [cmd,     setCmd]     = useState('AL');
  const [litres,  setLitres]  = useState('10');
  const [validity,setValidity]= useState('');
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [pubLog,  setPubLog]  = useState([]);
  const [respLog, setRespLog] = useState(null);
  const [showOk,  setShowOk]  = useState(false);

  const COMMANDS = [
    { id:'AL', label:'Add Litres',      emoji:'💧', hasL:true,  hasD:false,
      desc:'Increase water balance without changing validity' },
    { id:'EV', label:'Extend Validity', emoji:'📅', hasL:false, hasD:true,
      desc:'Increase validity period without changing litres' },
    { id:'RL', label:'Reverse Litres',  emoji:'↩️', hasL:true,  hasD:false,
      desc:'Deduct previously added litres (admin correction)' },
    { id:'RV', label:'Reverse Validity',emoji:'⏪', hasL:false, hasD:true,
      desc:'Reduce validity days (admin correction/fraud)' },
  ];

  const selCmd = COMMANDS.find(c => c.id === cmd);

  const buildPayload = () => {
    const txn = genTxnId();
    const IM  = mac.toLowerCase();
    const V   = validity || validityDate(90);
    if (cmd === 'AL') return { IM, CMD:'AL', L:litres, X:txn, OW:'Y' };
    if (cmd === 'EV') return { IM, CMD:'EV', V, X:txn, OW:'Y' };
    if (cmd === 'RL') return { IM, CMD:'RL', L:litres, X:txn, OW:'Y' };
    if (cmd === 'RV') return { IM, CMD:'RV', V, X:txn, OW:'Y' };
    return { IM, CMD:cmd };
  };

  const handleSend = () => {
    if (!connected || sending) return;
    const pl      = buildPayload();
    const payStr  = JSON.stringify(pl);
    const topic   = CMD_TOPIC(mac);
    const respTop = RESP_TOPIC(mac);

    setSending(true);
    setShowOk(false);
    setRespLog(null);
    setPubLog([`📡 PUB ${topic}`, `📎 ${payStr}`]);

    const unsub = subscribe(respTop, async (msg) => {
      try {
        let json;
        const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
        if (parsed.VAL && /^[0-9a-fA-F]+$/.test(parsed.VAL) && k2) {
          const decHex = await aesDecrypt(parsed.VAL, k2);
          json = JSON.parse(hexToString(decHex));
        } else {
          json = parsed;
        }
        if (json.CMD === cmd || (json.ERR && (json.CMD === 'AL' || json.CMD === cmd))) {
          setRespLog(json);
          if (json.ERR) {
            setRespLog({ ...json, _isError: true });
          }
          setShowOk(true);
          setSending(false);
          setSent(true);
          const txHistory = JSON.parse(localStorage.getItem('hoags_txn_history')||'[]');
          txHistory.unshift({
            type: cmd, device: device.friendlyname, mac,
            payload: pl, response: json,
            time: new Date().toISOString(),
          });
          localStorage.setItem('hoags_txn_history', JSON.stringify(txHistory.slice(0,100)));
          setTimeout(() => { setSent(false); setShowOk(false); }, 8000);
          setTimeout(() => unsub(), 100);
        }
      } catch(e) { console.error(`[${cmd}] response error:`, e); }
    });

    if (k2) {
      (async () => {
        try {
          const plainHex = stringToHex(payStr);
          const encrypted = await aesEncrypt(plainHex, k2);
          console.log(`[${cmd}] Encrypted → ${encrypted.substring(0, 40)}...`);
          publish(topic, encrypted);
        } catch (e) {
          console.error(`[${cmd}] encrypt error, fallback plaintext:`, e);
          publish(topic, payStr);
        }
      })();
    } else {
      publish(topic, payStr);
    }

    setTimeout(() => {
      setSending(false);
      unsub();
    }, 25000);
  };

  const inputStyle = {
    width:'100%', padding:'10px 12px', borderRadius:10,
    border:'1.5px solid rgba(0,0,0,.12)', fontSize:14,
    fontFamily:'inherit', background:'#f9f9f9',
    outline:'none', boxSizing:'border-box', marginTop:6,
  };

  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, zIndex:9999,
      background:'rgba(0,0,0,0.55)', backdropFilter:'blur(16px)',
      display:'flex', alignItems:'flex-end', justifyContent:'center',
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'#fff', borderRadius:'24px 24px 0 0',
        padding:'24px 20px 36px', width:'100%', maxWidth:520,
        boxShadow:'0 -20px 60px rgba(0,0,0,0.25)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",
        maxHeight:'90vh', overflowY:'auto',
      }}>
        <div style={{ width:36, height:4, borderRadius:2, background:'#e5e5ea', margin:'0 auto 18px' }}/>

        {/* iOS success popup */}
        {showOk && respLog && (
          <div style={{
            position:'fixed', inset:0, zIndex:99999,
            display:'flex', alignItems:'center', justifyContent:'center',
            background:'rgba(0,0,0,0.4)', backdropFilter:'blur(8px)',
            animation:'fadeUp .2s ease',
          }}>
            <div style={{
              background:'#fff', borderRadius:28,
              padding:'36px 32px 28px', textAlign:'center',
              width:280, boxShadow:'0 24px 80px rgba(0,0,0,0.3)',
              animation:'fadeUp .3s cubic-bezier(.4,0,.2,1)',
            }}>
              <div style={{
                width:72, height:72, borderRadius:'50%', margin:'0 auto 18px',
                background: respLog._isError
                  ? 'linear-gradient(135deg,#FF3B30,#CC0000)'
                  : 'linear-gradient(135deg,#34C759,#248A3D)',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow: respLog._isError
                  ? '0 8px 32px rgba(255,59,48,.45)'
                  : '0 8px 32px rgba(52,199,89,.45)',
                animation:'popIn .4s cubic-bezier(.4,0,.2,1)',
              }}>
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                  {respLog._isError
                    ? <><path d="M12 8v4M12 16h.01" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/><circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2"/></>
                    : <path d="M5 12l5 5L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
                </svg>
              </div>
              <div style={{ fontSize:18, fontWeight:800, color:'#1c1c1e', marginBottom:8 }}>
                {respLog._isError ? 'Command Failed' : 'Done!'}
              </div>
              <div style={{ fontSize:13, color:'#8E8E93', lineHeight:1.6, marginBottom:20 }}>
                {respLog._isError
                  ? <div style={{color:'#FF3B30',fontWeight:600}}>{respLog.ERR}</div>
                  : <>
                    {respLog.NL  != null && <div>💧 New Litres: <b style={{color:'#1c1c1e'}}>{respLog.NL}</b></div>}
                    {respLog.NV  != null && <div>📅 New Validity: <b style={{color:'#1c1c1e'}}>{respLog.NV}</b></div>}
                    {respLog.X   != null && <div style={{fontSize:11,color:'#C7C7CC',marginTop:6}}>Txn: {respLog.X}</div>}
                  </>}
              </div>
              <button onClick={()=>setShowOk(false)} style={{
                width:'100%', padding:'12px', borderRadius:14, border:'none',
                background:'linear-gradient(135deg,#34C759,#248A3D)',
                color:'#fff', fontWeight:700, fontSize:15,
                cursor:'pointer', fontFamily:'inherit',
                boxShadow:'0 4px 14px rgba(52,199,89,.4)',
              }}>OK</button>
            </div>
          </div>
        )}

        <div style={{ fontSize:18, fontWeight:800, color:'#1c1c1e', marginBottom:3 }}>Send Request</div>
        <div style={{ fontSize:12, color:'#8E8E93', marginBottom:16 }}>{device.friendlyname} · {mac}</div>

        {/* 2x2 command grid */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
          {COMMANDS.map(c => (
            <div key={c.id} onClick={() => { setCmd(c.id); setShowOk(false); setRespLog(null); setPubLog([]); }} style={{
              padding:'12px 14px', borderRadius:14, cursor:'pointer',
              border: cmd===c.id ? '2px solid #007AFF' : '1.5px solid rgba(0,0,0,.08)',
              background: cmd===c.id ? 'rgba(0,122,255,.06)' : '#fafafa',
              transition:'all .15s',
            }}>
              <div style={{ fontSize:22, marginBottom:6 }}>{c.emoji}</div>
              <div style={{ fontSize:13, fontWeight:700, color:cmd===c.id?'#007AFF':'#1c1c1e',
                marginBottom:3 }}>{c.label}</div>
              <div style={{ fontSize:10, color:'#8E8E93', lineHeight:1.4 }}>{c.desc}</div>
            </div>
          ))}
        </div>

        {/* Litres input */}
        {selCmd?.hasL && (
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#48484a' }}>
              {cmd==='RL' ? 'Litres to Reverse' : 'Litres to Add'}
            </label>
            <input type="number" min="1" value={litres}
              onChange={e=>setLitres(e.target.value)}
              placeholder="e.g. 10" style={inputStyle}/>
          </div>
        )}

        {/* Date input */}
        {selCmd?.hasD && (
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#48484a' }}>
              {cmd==='RV' ? 'Reverse to Date (DDMMYY)' : 'New Validity Date (DDMMYY)'}
            </label>
            <input type="text" value={validity} maxLength={6}
              onChange={e=>setValidity(e.target.value.replace(/\D/g,''))}
              placeholder={`e.g. ${validityDate(90)}`} style={inputStyle}/>
            <div style={{ fontSize:10, color:'#8E8E93', marginTop:4 }}>
              Format: DDMMYY · Leave blank = +90 days from today
            </div>
          </div>
        )}

        <button onClick={handleSend} disabled={!connected||sending} style={{
          width:'100%', padding:'14px', borderRadius:14, border:'none',
          background: sent?'linear-gradient(135deg,#34C759,#248A3D)'
            : !connected?'#c7c7cc'
            : 'linear-gradient(135deg,#007AFF,#0056d3)',
          color:'#fff', fontWeight:700, fontSize:15,
          cursor:!connected||sending?'not-allowed':'pointer', fontFamily:'inherit',
          boxShadow:connected&&!sending?'0 4px 16px rgba(0,122,255,.35)':'none',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8, transition:'all .3s',
        }}>
          {sent?'✅ Sent!':sending?'📡 Waiting for response…':'📤 Send Command'}
        </button>
        <button onClick={onClose} style={{
          width:'100%', marginTop:10, padding:'12px', borderRadius:14,
          border:'1.5px solid rgba(0,0,0,.1)', background:'transparent',
          color:'#8E8E93', fontWeight:600, fontSize:14, cursor:'pointer', fontFamily:'inherit',
        }}>Cancel</button>
      </div>
    </div>
  );
}



// ── Global styles injected once ────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  @keyframes iosBar    { from{opacity:1} to{opacity:.15} }
  @keyframes fadeUp    { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spinAnim  { to{transform:rotate(360deg)} }
  @keyframes pulseGlow { 0%,100%{opacity:1} 50%{opacity:.45} }
  @keyframes dotPop    { 0%,100%{transform:scale(1)} 50%{transform:scale(1.5)} }
  @keyframes popIn     { 0%{transform:scale(.6);opacity:0} 70%{transform:scale(1.1)} 100%{transform:scale(1);opacity:1} }
  @keyframes shimmer   {
    0%   { background-position: -600px 0 }
    100% { background-position:  600px 0 }
  }
  @keyframes waterFill {
    0%   { transform: translateY(100%); }
    100% { transform: translateY(0%); }
  }
  @keyframes waveMove {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  @keyframes dropFall {
    0%   { transform: translateY(0) scaleY(1); opacity:1; }
    70%  { transform: translateY(18px) scaleY(1.2); opacity:0.7; }
    85%  { transform: translateY(20px) scaleY(0.4) scaleX(1.6); opacity:0.4; }
    100% { transform: translateY(20px) scaleY(0); opacity:0; }
  }
  @keyframes dropForm {
    0%,40% { transform: scaleY(0); opacity:0; transform-origin: top center; }
    60%    { transform: scaleY(1); opacity:1; transform-origin: top center; }
    100%   { transform: scaleY(1); opacity:1; }
  }
  @keyframes ledBlink {
    0%,100% { opacity:1; } 50% { opacity:0.3; }
  }
  @keyframes ripple {
    0%   { transform:scale(0.3); opacity:0.8; }
    100% { transform:scale(2.2); opacity:0; }
  }
  .ds-metric-card {
    background: #fff;
    border-radius: 20px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-shadow: 0 2px 16px rgba(0,0,0,.07);
    border: 1px solid rgba(0,0,0,.06);
    transition: box-shadow .22s ease, transform .22s ease;
    animation: fadeUp .4s ease both;
    position: relative;
    overflow: hidden;
  }
  .ds-metric-card:hover {
    box-shadow: 0 10px 36px rgba(0,0,0,.12);
    transform: translateY(-3px);
  }
  .ds-metric-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    border-radius: 20px 20px 0 0;
    opacity: 0;
    transition: opacity .3s;
  }
  .ds-metric-card:hover::before { opacity: 1; }
  .ds-row-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 11px 14px;
    background: rgba(0,0,0,.025);
    border-radius: 12px;
    margin-bottom: 7px;
    transition: background .15s;
  }
  .ds-row-item:hover { background: rgba(0,122,255,.05); }
  .ds-skeleton {
    background: linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%);
    background-size: 600px 100%;
    animation: shimmer 1.5s infinite linear;
    border-radius: 8px;
  }
  @media (max-width: 600px) {
    .ds-grid-2 { grid-template-columns: 1fr 1fr !important; }
    .ds-grid-3 { grid-template-columns: 1fr 1fr !important; }
    .ds-grid-4 { grid-template-columns: 1fr 1fr !important; }
    .ds-hero   { flex-direction: column !important; align-items: flex-start !important; }
    .ds-topbar { flex-wrap: wrap !important; gap: 10px !important; }
  }
  @media (max-width: 380px) {
    .ds-grid-2 { grid-template-columns: 1fr !important; }
    .ds-grid-4 { grid-template-columns: 1fr !important; }
  }
`;

// ── SVG Icons (water-purifier themed) ─────────────────────────────────────
const Icons = {
  // Water drop with quality indicator
  WaterQuality: ({ color = '#007AFF', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2C12 2 5 9.5 5 14a7 7 0 0014 0C19 9.5 12 2 12 2Z" fill={color} opacity=".15" stroke={color} strokeWidth="1.5"/>
      <path d="M9 14a3 3 0 006 0" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="12" cy="11" r="1" fill={color}/>
    </svg>
  ),
  // Filter membrane
  Filter: ({ color = '#007AFF', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="2.5" rx="1.25" fill={color} opacity=".3"/>
      <rect x="5" y="8.5" width="14" height="2" rx="1" fill={color} opacity=".5"/>
      <rect x="7" y="12.5" width="10" height="2" rx="1" fill={color} opacity=".7"/>
      <path d="M10 16.5v3l2 1.5 2-1.5v-3" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  ),
  // Inner TDS filter
  TDS: ({ color = '#AF52DE', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1.5" opacity=".3"/>
      <circle cx="12" cy="12" r="5" stroke={color} strokeWidth="1.5" opacity=".6"/>
      <circle cx="12" cy="12" r="2" fill={color}/>
      <path d="M12 4v2M12 18v2M4 12h2M18 12h2" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // Purification pump
  Pump: ({ color = '#34C759', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="7" stroke={color} strokeWidth="1.5"/>
      <path d="M9 12h6M12 9v6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <path d="M5 8H3M21 8h-2M5 16H3M21 16h-2" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity=".5"/>
    </svg>
  ),
  // Thermometer hot
  HotWater: ({ color = '#FF3B30', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M14 14.18V5a2 2 0 00-4 0v9.18a4 4 0 104 0Z" stroke={color} strokeWidth="1.5"/>
      <circle cx="12" cy="17" r="1.5" fill={color}/>
      <path d="M17 6.5c1.5 1 1.5 3 0 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity=".5"/>
      <path d="M19 5c2 1.5 2 5 0 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity=".3"/>
    </svg>
  ),
  // Warm water
  WarmWater: ({ color = '#FF9F0A', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M14 14.18V5a2 2 0 00-4 0v9.18a4 4 0 104 0Z" stroke={color} strokeWidth="1.5"/>
      <circle cx="12" cy="17" r="1.5" fill={color}/>
      <path d="M16 9c.5 1 .5 2.5 0 3.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity=".5"/>
    </svg>
  ),
  // UV lamp / sterilize
  UV: ({ color = '#AF52DE', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3" fill={color} opacity=".2" stroke={color} strokeWidth="1.5"/>
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12"
        stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // Tank water level
  Tank: ({ color = '#007AFF', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="18" rx="3" stroke={color} strokeWidth="1.5"/>
      <rect x="4" y="13" width="16" height="8" rx="0" fill={color} opacity=".15"/>
      <path d="M4 13h16" stroke={color} strokeWidth="1"/>
      <path d="M8 8h8M8 11h5" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity=".4"/>
      <path d="M10 17h4" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity=".6"/>
    </svg>
  ),
  // CPU/chip
  Chip: ({ color = '#8E8E93', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="6" y="6" width="12" height="12" rx="2" stroke={color} strokeWidth="1.5"/>
      <rect x="9" y="9" width="6" height="6" rx="1" fill={color} opacity=".2"/>
      <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // Shield/sensor
  Sensor: ({ color = '#34C759', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 3L4 7v5c0 4.5 3.5 8.5 8 9.5 4.5-1 8-5 8-9.5V7L12 3Z"
        fill={color} opacity=".12" stroke={color} strokeWidth="1.5"/>
      <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Back arrow
  Back: ({ color = '#007AFF', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Refresh
  Refresh: ({ color = '#fff', size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M1 4v6h6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3.51 15a9 9 0 102.13-9.36L1 10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Terminal
  Terminal: ({ color = '#007AFF', size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="2" y="3" width="20" height="18" rx="3" stroke={color} strokeWidth="1.5"/>
      <path d="M7 9l4 3-4 3M13 15h4" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Trash
  Trash: ({ color = '#8E8E93', size = 11 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Check
  Check: ({ color = '#34C759', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" fill={color} opacity=".15" stroke={color} strokeWidth="1.5"/>
      <path d="M8 12l3 3 5-5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Alert
  Alert: ({ color = '#FF9F0A', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" fill={color} opacity=".12" stroke={color} strokeWidth="1.5"/>
      <path d="M12 8v4M12 16h.01" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  // Radio signal
  Signal: ({ color = '#8E8E93', size = 32 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M1 6c5.5-5.5 14.5-5.5 20 0" stroke={color} strokeWidth="1.8" strokeLinecap="round" opacity=".3"/>
      <path d="M4 9c4-4 11-4 15 0" stroke={color} strokeWidth="1.8" strokeLinecap="round" opacity=".5"/>
      <path d="M7.5 12.5c2.5-2.5 6.5-2.5 9 0" stroke={color} strokeWidth="1.8" strokeLinecap="round" opacity=".7"/>
      <circle cx="12" cy="17" r="2" fill={color}/>
      <path d="M12 19v3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
};

// ── Water Purifier Icon — uses real product image ─────────────────────────
function WaterPurifierIcon({ size = 42 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.22, flexShrink: 0,
      overflow: 'hidden',
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      background: '#1a1a1a',
    }}>
      <img
        src="/havellspurifier.jpg"
        alt="Havells Purifier"
        style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
      />
    </div>
  );
}

const wqLabel = v => ({ G:'Good', A:'Average', P:'Poor' }[v] ?? v ?? '—');
const wqColor = v => ({ G:'#34C759', A:'#FF9F0A', P:'#FF3B30' }[v] ?? '#8E8E93');
const wqBg    = v => ({ G:'rgba(52,199,89,.1)', A:'rgba(255,159,10,.1)', P:'rgba(255,59,48,.08)' }[v] ?? 'rgba(142,142,147,.08)');
const onOff   = v => v === '2' ? 'On' : v === '1' ? 'Off' : v ?? '—';
const tankLevel = v => {
  // Convert raw device value to fill percentage
  // Device values: '1'=Low, '2'=Medium, '3'=High, '4'=Low→Med
  // Map to approximate %: Low≈20%, Med≈55%, High≈90%, Low→Med≈32%
  const pctMap = { '1':20, '2':55, '3':95, '4':32 };
  const pct = pctMap[v];
  if (pct == null) return null; // unknown/missing — show nothing
  return pct >= 90 ? 'High' : null; // below 90% show nothing, just label text
};
const tankColor = v => {
  const pctMap = { '1':20, '2':55, '3':95, '4':32 };
  const pct = pctMap[v];
  if (pct == null) return '#8E8E93';
  return pct >= 90 ? '#34C759' : '#FF3B30';
};

function parseTemp(tmp) {
  if (!tmp || tmp.length < 4) return { hot: null, warm: null };
  return {
    hot:  parseInt(tmp.slice(0, 2), 10),
    warm: parseInt(tmp.slice(2, 4), 10),
  };
}

// ── iOS Spinner ────────────────────────────────────────────────────────────
function IOSSpinner({ size = 28, color = '#007AFF' }) {
  return (
    <div style={{ position:'relative', width:size, height:size, flexShrink:0, transform:'translateY(-2px)' }}>
      {Array.from({ length:12 }).map((_,i) => (
        <div key={i} style={{
          position:'absolute', top:'50%', left:'50%',
          width: size * 0.085, height: size * 0.26,
          borderRadius: size * 0.05,
          background: color,
          transformOrigin:'50% 140%',
          transform:`rotate(${i*30}deg) translateX(-50%)`,
          animation:`iosBar 1s linear ${-(12-i)/12}s infinite`,
        }}/>
      ))}
    </div>
  );
}

// ── Metric Card ────────────────────────────────────────────────────────────
function MetricCard({ Icon, label, value, sub, color, accent, delay = 0, badge }) {
  return (
    <div className="ds-metric-card" style={{ animationDelay:`${delay}s` }}>
      <div style={{
        position:'absolute', top:0, left:0, right:0, height:3,
        background:`linear-gradient(90deg,${color},${accent||color})`,
        borderRadius:'20px 20px 0 0',
        opacity:.7,
      }}/>
      {/* Icon */}
      <div style={{
        width:46, height:46, borderRadius:14,
        background:`linear-gradient(135deg,${color}18,${color}08)`,
        border:`1px solid ${color}20`,
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <Icon color={color} size={22}/>
      </div>
      {/* Value */}
      <div>
        <div style={{ fontSize:11, color:'#8E8E93', fontWeight:500, marginBottom:5, letterSpacing:.2 }}>
          {label.toUpperCase()}
        </div>
        {value == null
          ? <div className="ds-skeleton" style={{ height:28, width:'60%' }}/>
          : <div style={{
              fontSize: String(value).length > 5 ? 20 : 26,
              fontWeight:800, color:'#1c1c1e', letterSpacing:-.5, lineHeight:1,
              display:'flex', alignItems:'center', gap:8,
            }}>
              {value}
              {badge}
            </div>
        }
        {sub && value != null && (
          <div style={{ fontSize:11, color:'#8E8E93', marginTop:5, fontWeight:400 }}>{sub}</div>
        )}
      </div>
    </div>
  );
}

// ── Section Header ─────────────────────────────────────────────────────────
function SectionHead({ Icon, color, title, sub }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14, marginTop:28 }}>
      <div style={{
        width:38, height:38, borderRadius:11,
        background:`linear-gradient(135deg,${color}22,${color}10)`,
        border:`1px solid ${color}25`,
        display:'flex', alignItems:'center', justifyContent:'center',
        flexShrink:0,
      }}>
        <Icon color={color} size={18}/>
      </div>
      <div>
        <div style={{ fontSize:16, fontWeight:700, color:'#1c1c1e', letterSpacing:-.3 }}>{title}</div>
        {sub && <div style={{ fontSize:12, color:'#8E8E93', marginTop:1 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Info Row ───────────────────────────────────────────────────────────────
function InfoRow({ label, value, mono, pill }) {
  return (
    <div className="ds-row-item">
      <span style={{ fontSize:13, color:'#8E8E93', fontWeight:500 }}>{label}</span>
      {pill
        ? pill
        : <span style={{
            fontSize:13, color:'#1c1c1e', fontWeight:600,
            fontFamily: mono ? '"SF Mono",monospace' : 'inherit',
          }}>{value ?? '—'}</span>
      }
    </div>
  );
}

// ── Status Pill ────────────────────────────────────────────────────────────
function StatusPill({ ok, onLabel='Normal', offLabel='Fault', unknown }) {
  if (unknown) return (
    <span style={{ fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:20,
      background:'rgba(142,142,147,.1)',color:'#8E8E93' }}>—</span>
  );
  return (
    <span style={{
      fontSize:11, fontWeight:700, padding:'3px 12px', borderRadius:20,
      background: ok ? 'rgba(52,199,89,.12)' : 'rgba(255,59,48,.1)',
      color: ok ? '#34C759' : '#FF3B30',
      display:'inline-flex', alignItems:'center', gap:5,
    }}>
      <span style={{
        width:5, height:5, borderRadius:'50%',
        background: ok ? '#34C759' : '#FF3B30',
        animation: ok ? 'pulseGlow 1.5s ease infinite' : 'none',
      }}/>
      {ok ? onLabel : offLabel}
    </span>
  );
}

// ── Packet Progress ────────────────────────────────────────────────────────
function PacketDots({ received }) {
  return (
    <div style={{ display:'flex', gap:10, alignItems:'center' }}>
      {[1,2,3,4].map(n => {
        const got = received.has(n);
        return (
          <div key={n} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
            <div style={{
              width:10, height:10, borderRadius:'50%',
              background: got ? '#34C759' : '#e5e5ea',
              transition:'background .4s, box-shadow .4s',
              boxShadow: got ? '0 0 8px rgba(52,199,89,.7)' : 'none',
              animation: got ? 'dotPop .3s ease' : 'none',
            }}/>
            <span style={{ fontSize:9, fontWeight:600, color: got ? '#34C759' : '#C7C7CC' }}>
              Q{n}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Debug Panel ────────────────────────────────────────────────────────────
const LOG_COLORS = {
  cmd_pub:  { label:'CMD PUB',  bg:'rgba(0,122,255,.18)',  color:'#007AFF' },
  resp_sub: { label:'RESP SUB', bg:'rgba(175,82,222,.18)', color:'#AF52DE' },
  pkt_rx:   { label:'PKT RX',  bg:'rgba(52,199,89,.18)',  color:'#34C759' },
  pkt_skip: { label:'SKIPPED', bg:'rgba(255,159,10,.18)', color:'#FF9F0A' },
  timeout:  { label:'TIMEOUT', bg:'rgba(255,59,48,.18)',  color:'#FF3B30' },
  info:     { label:'INFO',    bg:'rgba(142,142,147,.15)',color:'#8E8E93' },
  err:      { label:'ERROR',   bg:'rgba(255,59,48,.18)',  color:'#FF3B30' },
};

function DebugPanel({ logs, onClear }) {
  const [open, setOpen] = useState(true);
  const bottomRef = useRef(null);
  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior:'smooth' });
    }
  }, [logs, open]);

  return (
    <div style={{
      borderRadius:16, overflow:'hidden',
      background:'#0d1117',
      border:'1px solid rgba(255,255,255,.08)',
      boxShadow:'0 4px 24px rgba(0,0,0,.25)',
      marginBottom:16,
    }}>
      {/* header */}
      <div onClick={() => setOpen(o=>!o)} style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'11px 16px', cursor:'pointer', userSelect:'none',
        borderBottom: open ? '1px solid rgba(255,255,255,.06)' : 'none',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <Icons.Terminal color="#007AFF" size={14}/>
          <span style={{ fontSize:12, fontWeight:600, color:'#e6edf3', letterSpacing:.3 }}>
            MQTT Debug Console
          </span>
          {logs.length > 0 && (
            <span style={{
              fontSize:10, fontWeight:700, padding:'1px 8px', borderRadius:20,
              background:'rgba(0,122,255,.25)', color:'#60aaff',
            }}>{logs.length}</span>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={e=>{e.stopPropagation();onClear();}} style={{
            background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.1)',
            borderRadius:6, padding:'3px 9px', cursor:'pointer',
            color:'#8b949e', fontSize:11, display:'flex', alignItems:'center', gap:4,
            fontFamily:'inherit',
          }}>
            <Icons.Trash color="#8b949e" size={11}/> Clear
          </button>
          <span style={{ color:'#30363d', fontSize:11, fontWeight:600 }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {/* entries */}
      {open && (
        <div style={{ maxHeight:240, overflowY:'auto', fontFamily:'"SF Mono",monospace' }}>
          {logs.length === 0
            ? <div style={{ padding:'20px', color:'#484f58', fontSize:12, textAlign:'center' }}>
                No events yet — press Refresh to send DS command…
              </div>
            : logs.map((log, i) => {
                const t = LOG_COLORS[log.type] || { label:log.type, bg:'rgba(255,255,255,.1)', color:'#fff' };
                return (
                  <div key={i} style={{
                    display:'flex', alignItems:'flex-start', gap:9,
                    padding:'5px 16px', borderBottom:'1px solid rgba(255,255,255,.04)',
                  }}>
                    <span style={{ color:'#484f58', fontSize:10, minWidth:56, paddingTop:2, flexShrink:0 }}>
                      {log.time}
                    </span>
                    <span style={{
                      fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:5,
                      background:t.bg, color:t.color,
                      minWidth:66, textAlign:'center', flexShrink:0, letterSpacing:.4,
                    }}>{t.label}</span>
                    <span style={{ fontSize:11, color:'#adbac7', wordBreak:'break-all', lineHeight:1.65 }}>
                      {log.msg}
                    </span>
                  </div>
                );
              })
          }
          <div ref={bottomRef}/>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════════════
// Format a single RTD response field for display.
// T   -> RTC working/not working (instead of raw timestamp)
// RAM -> percentage
// CPU -> percentage
// S   -> hidden entirely (status code, not user-facing)
// Anything else falls back to its raw value.
function formatRtdField(key, val) {
  if (key === 'S') return null; // hide

  if (key === 'T') {
    // Expected shape: DDMMYY HH:MM:SS (e.g. "260626 00:48:30")
    const str = String(val || '').trim();
    const looksValid = /^\d{6}\s+\d{2}:\d{2}:\d{2}$/.test(str) && str.slice(0,6) !== '000000';
    return {
      label: 'RTC',
      display: looksValid ? 'Working' : 'Not Working',
      color: looksValid ? '#34C759' : '#FF3B30',
      raw: str,
    };
  }

  if (key === 'RAM' || key === 'CPU') {
    const num = parseFloat(val);
    return {
      label: key,
      display: Number.isFinite(num) ? `${num}% used` : String(val),
      color: '#1c1c1e',
      pct: Number.isFinite(num) ? num : null,
      barColor: '#34C759',
    };
  }

  if (key === 'HEAP') {
    // raw=9 means 9% of 160KB is free → freeKB = 9*160/100 = 14.4KB
    const raw = parseFloat(val);
    const free = Number.isFinite(raw) ? parseFloat((raw * 160 / 100).toFixed(1)) : null;
    const used = free != null ? Math.round(((160 - free) / 160) * 100) : null;
    return {
      label: 'FREE HEAP',
      display: free != null ? free + 'KB / 160KB' : String(val),
      color: '#1c1c1e',
      pct: used,
      barColor: '#FF9F0A',
    };
  }

  return { label: key, display: String(val), color: '#1c1c1e' };
}

// ── Diagnostics — real encrypted RTD request/response (same K2 flow as DS) ───
// Sends {"IM":mac,"CMD":"RTD"} encrypted with K2 over MQTT and renders
// whatever the device sends back. No more hardcoded ds.PES/UES/VES/HES checks.
function DiagnosticsModal({ device, mac, connected, publish, subscribe, onClose, k2 }) {
  const [phase,   setPhase]   = React.useState('sending'); // sending | waiting | done | error
  const [respLog, setRespLog] = React.useState(null);
  const [errMsg,  setErrMsg]  = React.useState('');
  const unsubRef = React.useRef(null);
  const timerRef  = React.useRef(null);

  React.useEffect(() => {
    if (!connected) { setPhase('error'); setErrMsg('MQTT not connected.'); return; }

    const cmdTopic  = CMD_TOPIC(mac);
    const respTopic = RESP_TOPIC(mac);
    const payload   = { IM: mac.toLowerCase(), CMD: 'RTD' };
    const payStr     = JSON.stringify(payload);

    setPhase('sending'); setRespLog(null); setErrMsg('');

    // Subscribe for response BEFORE publishing, same pattern as DS/SendRequestSheet
    const unsub = subscribe(respTopic, async (msg) => {
      try {
        let json;
        const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
        if (parsed.VAL && /^[0-9a-fA-F]+$/.test(parsed.VAL) && parsed.VAL.length > 20 && k2) {
          const decHex = await aesDecrypt(parsed.VAL, k2);
          json = JSON.parse(hexToString(decHex));
        } else {
          json = parsed;
        }
        if (json.CMD === 'RTD' || json.ERR) {
          setRespLog(json);
          setPhase('done');
          clearTimeout(timerRef.current);
          setTimeout(() => unsub(), 100);
        }
      } catch (e) {
        console.error('[RTD] response parse error:', e);
      }
    });
    unsubRef.current = unsub;

    // Encrypt + publish, same as DS/SendRequest. Falls back to plaintext if no K2.
    (async () => {
      try {
        if (k2) {
          const plainHex  = stringToHex(payStr);
          const encrypted = await aesEncrypt(plainHex, k2);
          publish(cmdTopic, encrypted);
        } else {
          publish(cmdTopic, payStr);
        }
        setPhase('waiting');
      } catch (e) {
        console.error('[RTD] encrypt error, fallback plaintext:', e);
        publish(cmdTopic, payStr);
        setPhase('waiting');
      }
    })();

    // Timeout after 25s if no response
    timerRef.current = setTimeout(() => {
      setPhase(p => p === 'done' ? p : 'error');
      setErrMsg(e => e || 'No response from device within 25s.');
      unsub();
    }, 25000);

    return () => {
      clearTimeout(timerRef.current);
      if (unsubRef.current) unsubRef.current();
    };
  // eslint-disable-next-line
  }, []);

  const isError = phase === 'error' || (respLog && respLog.ERR);
  const done    = phase === 'done' || phase === 'error';

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9998, background:'transparent' }}/>
      <div onClick={e => e.stopPropagation()} style={{
        position:'absolute', top:'calc(100% + 12px)', left:'50%', transform:'translateX(-50%)',
        zIndex:9999,
        background:'#fff', borderRadius:22, padding:'24px 22px',
        width:340, maxWidth:'92vw', maxHeight:'70vh', overflowY:'auto',
        boxShadow:'0 24px 60px rgba(0,0,0,0.22)',
        border:'1px solid rgba(0,0,0,.06)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
      }}>
        {/* caret pointer to button above */}
        <div style={{
          position:'absolute', top:-8, left:'50%', marginLeft:-8,
          width:16, height:16, background:'#fff', transform:'rotate(45deg)',
          borderLeft:'1px solid rgba(0,0,0,.06)', borderTop:'1px solid rgba(0,0,0,.06)',
        }}/>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:10, marginBottom:22 }}>
          <div style={{
            width:46, height:46, borderRadius:14, flexShrink:0,
            background: !done
              ? 'linear-gradient(135deg,#007AFF,#0056d3)'
              : isError
                ? 'linear-gradient(135deg,#FF3B30,#CC0000)'
                : 'linear-gradient(135deg,#34C759,#30D158)',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow: !done ? '0 4px 16px rgba(0,122,255,.3)'
              : isError ? '0 4px 16px rgba(255,59,48,.4)' : '0 4px 16px rgba(52,199,89,.4)',
            transition:'background .5s',
          }}>
            {!done
              ? <div style={{ width:20, height:20, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,.3)', borderTopColor:'#fff', animation:'spinAnim .7s linear infinite' }}/>
              : isError
                ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 8v4M12 16h.01" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                    <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" fill="none"/>
                  </svg>
                : <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12l5 5L19 7" stroke="#fff" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
            }
          </div>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1c1c1e', letterSpacing:-.3 }}>
              {!done ? (phase==='sending' ? 'Sending RTD request…' : 'Waiting for device…')
                : isError ? 'Diagnostics Failed' : 'Diagnostics Complete'}
            </div>
            <div style={{ fontSize:12, color:'#8E8E93', marginTop:2 }}>
              {device.friendlyname} · {mac}
            </div>
          </div>
        </div>

        {/* Encrypted command preview removed per request */}

        {/* Response */}
        {done && (
          <div style={{
            background: isError ? 'rgba(255,59,48,.06)' : 'rgba(52,199,89,.06)',
            border: `1px solid ${isError ? 'rgba(255,59,48,.2)' : 'rgba(52,199,89,.2)'}`,
            borderRadius:14, padding:'14px 16px', marginBottom:20,
          }}>
            {isError ? (
              <div style={{ fontSize:13, color:'#FF3B30', fontWeight:600, lineHeight:1.6 }}>
                {respLog?.ERR || errMsg}
              </div>
            ) : respLog ? (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {Object.entries(respLog)
                  .filter(([k]) => k !== 'IM' && k !== 'CMD')
                  .map(([key, val]) => [key, formatRtdField(key, val)])
                  .filter(([, f]) => f !== null)
                  .map(([key, f]) => (
                    <div key={key} style={{ textAlign:'center' }}>
                      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:12, color:'#8E8E93', fontFamily:'monospace' }}>{f.label}</span>
                        <span style={{ fontSize:13, color:f.color, fontWeight:700, display:'flex', alignItems:'center', gap:6 }}>
                          {(key === 'T') && (
                            <span style={{ width:6, height:6, borderRadius:'50%', background:f.color, flexShrink:0 }}/>
                          )}
                          {f.display}
                        </span>
                      </div>
                      {f.pct != null && (
                        <div style={{ background:'rgba(0,0,0,.08)', borderRadius:999, height:6, overflow:'hidden', marginTop:6, width:'70%', marginLeft:'auto', marginRight:'auto' }}>
                          <div style={{
                            height:'100%', borderRadius:999,
                            width:`${Math.min(100, Math.max(0, f.pct))}%`,
                            background: f.barColor || '#007AFF',
                            transition:'width .6s ease',
                          }}/>
                        </div>
                      )}
                    </div>
                  ))}
                {Object.entries(respLog)
                  .filter(([k]) => k !== 'IM' && k !== 'CMD')
                  .map(([key, val]) => formatRtdField(key, val))
                  .filter(Boolean).length === 0 && (
                  <div style={{ fontSize:13, color:'#248A3D', fontWeight:600 }}>Device acknowledged — no fault flags returned.</div>
                )}
              </div>
            ) : null}
          </div>
        )}

        <button onClick={onClose} disabled={!done} style={{
          width:'100%', padding:'13px', borderRadius:14, border:'none',
          background: !done ? 'rgba(0,0,0,.06)'
            : isError ? 'linear-gradient(135deg,#FF3B30,#CC0000)' : 'linear-gradient(135deg,#34C759,#30D158)',
          color: !done ? '#8E8E93' : '#fff',
          fontWeight:700, fontSize:15, cursor: done ? 'pointer' : 'default',
          fontFamily:'inherit', transition:'all .4s',
          boxShadow: done ? (isError ? '0 4px 16px rgba(255,59,48,.35)' : '0 4px 16px rgba(52,199,89,.4)') : 'none',
        }}>
          {!done ? 'Waiting…' : 'Done'}
        </button>
      </div>
    </>
  );
}

// ── Error Stack — encrypted ESD request/response (same K2 flow as DS/RTD) ────
// Step 1: {"IM":mac,"CMD":"ESD","PAR":"-1"} → {"IM":mac,"CMD":"ESD","OCC":"0-1,1-1"}
//   OCC is comma-separated "<error_code>-<occurrence_count>" pairs.
// Step 2 (tap an error): {"IM":mac,"CMD":"ESD","PAR":"<error_code>"} → whatever
//   occurrence/resolution data the device sends back for that specific code.
const ERROR_CODES = {
  '0': 'PUMP', '1': 'LPS', '2': 'UV', '3': 'VALVE', '4': 'HEATER',
  '5': 'DRY', '6': 'CRASH', '7': 'UNAUTHORIZED MOMENT', '8': 'EXCESS USAGE',
  '9': 'PUMP TAMPERING', '10': 'FLOW TAMPERING',
};

function parseOcc(occStr) {
  if (!occStr) return [];
  return occStr.split(',').map(pair => {
    const [code, count] = pair.split('-');
    return { code, count: parseInt(count, 10) || 0, label: ERROR_CODES[code] || `CODE ${code}` };
  }).filter(e => e.count > 0);
}

// Device firmware sometimes drops the comma between two JSON fields when the
// preceding field's string value happens to end right at the closing quote
// — e.g. {"OCC":"0-2,5-1,""S":"T"} is missing a comma between OCC and S.
// Insert a comma wherever two quotes are adjacent with nothing but the start
// of a new "key": between them.
function repairMalformedDeviceJson(str) {
  return str.replace(/"\s*"(?=[A-Za-z0-9_]+"\s*:)/g, '","');
}

// Parses an "ES" occurrence/resolution field: comma-separated
// "<startYYYYMMDD>-<endYYYYMMDD>" ranges (may have a trailing empty entry
// from a trailing comma) into clean { start, end } pairs with formatted dates.
function fmtYmd(ymd) {
  if (!ymd || ymd.length !== 8) return ymd || '—';
  const y = ymd.slice(0,4), m = ymd.slice(4,6), d = ymd.slice(6,8);
  const dt = new Date(`${y}-${m}-${d}`);
  if (isNaN(dt)) return ymd;
  return dt.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}
function parseEsRanges(esStr) {
  if (!esStr) return [];
  return esStr.split(',')
    .map(r => r.trim())
    .filter(Boolean)
    .map(r => {
      const [start, end] = r.split('-');
      return { start: fmtYmd(start), end: end ? fmtYmd(end) : null };
    });
}

function ErrorStackModal({ device, mac, connected, publish, subscribe, onClose, k2 }) {
  const [phase, setPhase] = React.useState('sending'); // sending | waiting | list | detail | error
  const [errors, setErrors] = React.useState([]);
  const [selectedError, setSelectedError] = React.useState(null);
  const [detailResp, setDetailResp] = React.useState(null);
  const [errMsg, setErrMsg] = React.useState('');
  const unsubRef  = React.useRef(null);
  const timerRef  = React.useRef(null);
  const phaseRef  = React.useRef('list');

  const sendQuery = React.useCallback((par, expecting) => {
    if (!connected) { setPhase('error'); setErrMsg('MQTT not connected.'); return; }
    const cmdTopic  = CMD_TOPIC(mac);
    const respTopic = RESP_TOPIC(mac);
    const payload   = { IM: mac.toLowerCase(), CMD: 'ESD', PAR: String(par) };
    const payStr    = JSON.stringify(payload);
    phaseRef.current = expecting;
    setPhase('sending'); setErrMsg('');
    if (expecting === 'detail') setDetailResp(null);

    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    clearTimeout(timerRef.current);

    const unsub = subscribe(respTopic, async (msg) => {
      try {
        let json;
        const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
        if (parsed.VAL && /^[0-9a-fA-F]+$/.test(parsed.VAL) && parsed.VAL.length > 20 && k2) {
          const decHex = await aesDecrypt(parsed.VAL, k2);
          const decStr = hexToString(decHex);
          json = JSON.parse(repairMalformedDeviceJson(decStr));
        } else {
          json = parsed;
        }
        if (json.CMD !== 'ESD' && !json.ERR) return;
        clearTimeout(timerRef.current);
        if (phaseRef.current === 'list') {
          setErrors(parseOcc(json.OCC));
          setPhase('list');
        } else {
          setDetailResp(json);
          setPhase('detail');
        }
        setTimeout(() => unsub(), 100);
      } catch (e) { console.error('[ESD] response parse error:', e); }
    });
    unsubRef.current = unsub;

    (async () => {
      try {
        if (k2) {
          const plainHex  = stringToHex(payStr);
          const encrypted = await aesEncrypt(plainHex, k2);
          publish(cmdTopic, encrypted);
        } else {
          publish(cmdTopic, payStr);
        }
        setPhase('waiting');
      } catch (e) {
        console.error('[ESD] encrypt error, fallback plaintext:', e);
        publish(cmdTopic, payStr);
        setPhase('waiting');
      }
    })();

    timerRef.current = setTimeout(() => {
      setPhase(p => (p === 'list' || p === 'detail') ? p : 'error');
      setErrMsg(e => e || 'No response from device within 25s.');
      unsub();
    }, 25000);
  }, [connected, mac, publish, subscribe, k2]);

  React.useEffect(() => {
    sendQuery(-1, 'list');
    return () => {
      clearTimeout(timerRef.current);
      if (unsubRef.current) unsubRef.current();
    };
    // eslint-disable-next-line
  }, []);

  const handleSelectError = (err) => {
    setSelectedError(err);
    sendQuery(err.code, 'detail');
  };

  const busy = phase === 'sending' || phase === 'waiting';
  const showList = phase === 'list' && !selectedError;

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9998, background:'transparent' }}/>
      <div onClick={e => e.stopPropagation()} style={{
        position:'absolute', top:'calc(100% + 12px)', left:'50%', transform:'translateX(-50%)',
        zIndex:9999,
        background:'#fff', borderRadius:22, padding:'24px 22px',
        width:380, maxWidth:'92vw', maxHeight:'70vh', overflowY:'auto',
        boxShadow:'0 24px 60px rgba(0,0,0,0.22)',
        border:'1px solid rgba(0,0,0,.06)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
      }}>
        {/* caret pointer to button above */}
        <div style={{
          position:'absolute', top:-8, left:'50%', marginLeft:-8,
          width:16, height:16, background:'#fff', transform:'rotate(45deg)',
          borderLeft:'1px solid rgba(0,0,0,.06)', borderTop:'1px solid rgba(0,0,0,.06)',
        }}/>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:10, marginBottom:20 }}>
          <div style={{
            width:46, height:46, borderRadius:14, flexShrink:0,
            background: busy ? 'linear-gradient(135deg,#FF9F0A,#CC7A00)'
              : phase==='error' ? 'linear-gradient(135deg,#FF3B30,#CC0000)'
              : 'linear-gradient(135deg,#374151,#1F2937)',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            {busy
              ? <div style={{ width:20, height:20, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,.3)', borderTopColor:'#fff', animation:'spinAnim .7s linear infinite' }}/>
              : <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            }
          </div>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1c1c1e', letterSpacing:-.3 }}>
              {selectedError ? `${selectedError.label} Error` : 'Error Stack'}
            </div>
            <div style={{ fontSize:12, color:'#8E8E93', marginTop:2 }}>
              {device.friendlyname} · {mac}
            </div>
          </div>
        </div>

        {phase === 'error' && (
          <div style={{ background:'rgba(255,59,48,.06)', border:'1px solid rgba(255,59,48,.2)',
            borderRadius:14, padding:'14px 16px', marginBottom:16, fontSize:13, color:'#FF3B30', fontWeight:600 }}>
            {errMsg}
          </div>
        )}

        {busy && (
          <div style={{ textAlign:'center', padding:'20px 0', fontSize:13, color:'#8E8E93' }}>
            {phase==='sending' ? 'Sending request…' : 'Waiting for device…'}
          </div>
        )}

        {showList && (
          errors.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'#248A3D', fontWeight:600, fontSize:13 }}>
              No errors recorded — device is healthy.
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:8 }}>
              {errors.map(err => (
                <button key={err.code} onClick={() => handleSelectError(err)} style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'12px 16px', borderRadius:14, border:'1.5px solid rgba(0,0,0,.08)',
                  background:'#fafafa', cursor:'pointer', fontFamily:'inherit', textAlign:'left',
                }}>
                  <span style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>{err.label}</span>
                  <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20,
                    background:'rgba(255,59,48,.1)', color:'#FF3B30' }}>{err.count}×</span>
                </button>
              ))}
            </div>
          )
        )}

        {selectedError && phase === 'detail' && detailResp && (() => {
          const esRanges = parseEsRanges(detailResp.ES);
          const otherKeys = Object.entries(detailResp)
            .filter(([k]) => !['IM','CMD','PAR','S','ES'].includes(k));
          return (
            <div style={{
              background:'rgba(0,0,0,.03)', border:'1px solid rgba(0,0,0,.06)',
              borderRadius:14, padding:'14px 16px', marginBottom:16,
            }}>
              {esRanges.length > 0 && (
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom: otherKeys.length ? 10 : 0 }}>
                  {esRanges.map((r, i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8,
                      padding:'8px 0', borderBottom: i < esRanges.length-1 ? '1px solid rgba(0,0,0,.05)' : 'none' }}>
                      <div>
                        <div style={{ fontSize:9, color:'#8E8E93', fontWeight:700, letterSpacing:.4, marginBottom:2 }}>START DATE</div>
                        <div style={{ fontSize:12, color:'#1c1c1e', fontWeight:700 }}>{r.start}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:9, color:'#8E8E93', fontWeight:700, letterSpacing:.4, marginBottom:2 }}>END DATE</div>
                        <div style={{ fontSize:12, color: r.end ? '#1c1c1e' : '#FF9F0A', fontWeight:700 }}>{r.end || 'Ongoing'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {otherKeys.map(([key,val]) => (
                <div key={key} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0' }}>
                  <span style={{ fontSize:12, color:'#8E8E93', fontFamily:'monospace' }}>{key}</span>
                  <span style={{ fontSize:13, color:'#1c1c1e', fontWeight:700 }}>{String(val)}</span>
                </div>
              ))}
              {esRanges.length === 0 && otherKeys.length === 0 && (
                <div style={{ fontSize:13, color:'#248A3D', fontWeight:600 }}>Device acknowledged — no additional data.</div>
              )}
            </div>
          );
        })()}

        {selectedError && (
          <button onClick={() => { setSelectedError(null); setDetailResp(null); setPhase('list'); }} style={{
            width:'100%', marginBottom:10, padding:'11px', borderRadius:12,
            border:'1.5px solid rgba(0,0,0,.1)', background:'#fff', color:'#636366',
            fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit',
          }}>← Back to Error List</button>
        )}

        <button onClick={onClose} style={{
          width:'100%', padding:'13px', borderRadius:14, border:'none',
          background:'rgba(0,0,0,.06)', color:'#8E8E93',
          fontWeight:700, fontSize:15, cursor:'pointer', fontFamily:'inherit',
        }}>Done</button>
      </div>
    </>
  );
}

// Format a single Factory Reset (FR) response field for display.
// V=="0"  -> Validity shown as reset ("0")
// NL=="0" -> Remaining Litres shown as reset ("0")
// TL=="0" -> Total Litres shown as reset ("0")
// SD, LX, TMP, WF, S -> skipped, not shown to admin
function formatFrField(key, val) {
  const SKIP = ['SD', 'LX', 'TMP', 'WF', 'S'];
  if (SKIP.includes(key)) return null;

  if (key === 'V')  return { label: 'Validity',          display: String(val) };
  if (key === 'NL') return { label: 'Remaining Litres',  display: String(val) };
  if (key === 'TL') return { label: 'Total Litres',      display: String(val) };

  return { label: key, display: String(val) };
}

// ── Factory Reset — same encrypted request/response pattern as Diagnostics ──
// Request:  {"IM":mac,"CMD":"FR"}
// Response: {"IM":mac,"CMD":"FR","V":"0","NL":"0","TL":"0","SD":"7","LX":"0","TMP":"6045","WF":"D","S":"T"}
function FactoryResetModal({ device, mac, connected, publish, subscribe, onClose, k2 }) {
  const [confirmed, setConfirmed] = React.useState(false);
  const [phase,   setPhase]   = React.useState('idle'); // idle | sending | waiting | done | error
  const [respLog, setRespLog] = React.useState(null);
  const [errMsg,  setErrMsg]  = React.useState('');
  const unsubRef = React.useRef(null);
  const timerRef  = React.useRef(null);

  const sendFactoryReset = () => {
    if (!connected) { setPhase('error'); setErrMsg('MQTT not connected.'); return; }

    const cmdTopic  = CMD_TOPIC(mac);
    const respTopic = RESP_TOPIC(mac);
    const payload   = { IM: mac.toLowerCase(), CMD: 'FR' };
    const payStr     = JSON.stringify(payload);

    setPhase('sending'); setRespLog(null); setErrMsg('');

    const unsub = subscribe(respTopic, async (msg) => {
      try {
        let json;
        const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
        if (parsed.VAL && /^[0-9a-fA-F]+$/.test(parsed.VAL) && parsed.VAL.length > 20 && k2) {
          const decHex = await aesDecrypt(parsed.VAL, k2);
          json = JSON.parse(hexToString(decHex));
        } else {
          json = parsed;
        }
        if (json.CMD === 'FR' || json.ERR) {
          setRespLog(json);
          setPhase('done');
          clearTimeout(timerRef.current);
          setTimeout(() => unsub(), 100);
        }
      } catch (e) {
        console.error('[FR] response parse error:', e);
      }
    });
    unsubRef.current = unsub;

    (async () => {
      try {
        if (k2) {
          const plainHex  = stringToHex(payStr);
          const encrypted = await aesEncrypt(plainHex, k2);
          publish(cmdTopic, encrypted);
        } else {
          publish(cmdTopic, payStr);
        }
        setPhase('waiting');
      } catch (e) {
        console.error('[FR] encrypt error, fallback plaintext:', e);
        publish(cmdTopic, payStr);
        setPhase('waiting');
      }
    })();

    timerRef.current = setTimeout(() => {
      setPhase(p => p === 'done' ? p : 'error');
      setErrMsg(e => e || 'No response from device within 25s.');
      unsub();
    }, 25000);
  };

  React.useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      if (unsubRef.current) unsubRef.current();
    };
  }, []);

  const isError = phase === 'error' || (respLog && respLog.ERR);
  const done    = phase === 'done' || phase === 'error';
  const busy    = phase === 'sending' || phase === 'waiting';

  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, zIndex:9999,
      background:'rgba(0,0,0,0.6)', backdropFilter:'blur(18px)',
      display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:'#fff', borderRadius:26, padding:'28px 24px',
        width:360, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 32px 80px rgba(0,0,0,0.3)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:22 }}>
          <div style={{
            width:46, height:46, borderRadius:14, flexShrink:0,
            background: !done && !busy
              ? 'linear-gradient(135deg,#FF3B30,#CC0000)'
              : busy
                ? 'linear-gradient(135deg,#FF9F0A,#CC7A00)'
                : isError
                  ? 'linear-gradient(135deg,#FF3B30,#CC0000)'
                  : 'linear-gradient(135deg,#34C759,#30D158)',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow: '0 4px 16px rgba(0,0,0,.2)',
            transition:'background .5s',
          }}>
            {busy
              ? <div style={{ width:20, height:20, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,.3)', borderTopColor:'#fff', animation:'spinAnim .7s linear infinite' }}/>
              : done
                ? (isError
                  ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 8v4M12 16h.01" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                      <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" fill="none"/>
                    </svg>
                  : <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12l5 5L19 7" stroke="#fff" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>)
                : <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M3 12a9 9 0 1 0 9-9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
                    <path d="M3 4v5h5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
            }
          </div>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1c1c1e', letterSpacing:-.3 }}>
              {busy ? (phase==='sending' ? 'Sending Factory Reset…' : 'Waiting for device…')
                : done ? (isError ? 'Factory Reset Failed' : 'Factory Reset Complete')
                : 'Factory Reset'}
            </div>
            <div style={{ fontSize:12, color:'#8E8E93', marginTop:2 }}>
              {device.friendlyname} · {mac}
            </div>
          </div>
        </div>

        {/* Warning + confirmation, shown before sending */}
        {!busy && !done && (
          <>
            <div style={{ background:'rgba(255,59,48,.06)', border:'1px solid rgba(255,59,48,.18)',
              borderRadius:14, padding:'14px 16px', marginBottom:16 }}>
              <div style={{ fontSize:13, color:'#FF3B30', fontWeight:700, marginBottom:4 }}>⚠️ This cannot be undone</div>
              <div style={{ fontSize:12, color:'#48484a', lineHeight:1.6 }}>
                Factory reset will clear the device's validity and litres balance.
                The customer will need to recharge again afterwards.
              </div>
            </div>
            <label style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:18, cursor:'pointer' }}>
              <input type="checkbox" checked={confirmed} onChange={e=>setConfirmed(e.target.checked)}
                style={{ marginTop:2, width:16, height:16, accentColor:'#FF3B30' }}/>
              <span style={{ fontSize:12, color:'#48484a', lineHeight:1.5 }}>
                I understand this will reset the device and confirm I want to proceed.
              </span>
            </label>
          </>
        )}

        {/* Response */}
        {done && (
          <div style={{
            background: isError ? 'rgba(255,59,48,.06)' : 'rgba(52,199,89,.06)',
            border: `1px solid ${isError ? 'rgba(255,59,48,.2)' : 'rgba(52,199,89,.2)'}`,
            borderRadius:14, padding:'14px 16px', marginBottom:20,
          }}>
            {isError ? (
              <div style={{ fontSize:13, color:'#FF3B30', fontWeight:600, lineHeight:1.6 }}>
                {respLog?.ERR || errMsg}
              </div>
            ) : respLog ? (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {Object.entries(respLog)
                  .filter(([k]) => k !== 'IM' && k !== 'CMD')
                  .map(([key, val]) => [key, formatFrField(key, val)])
                  .filter(([, f]) => f !== null)
                  .map(([key, f]) => (
                    <div key={key} style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontSize:12, color:'#8E8E93' }}>{f.label}</span>
                      <span style={{ fontSize:13, color:'#1c1c1e', fontWeight:700 }}>{f.display}</span>
                    </div>
                  ))}
                {Object.entries(respLog)
                  .filter(([k]) => k !== 'IM' && k !== 'CMD')
                  .map(([key, val]) => formatFrField(key, val))
                  .filter(Boolean).length === 0 && (
                  <div style={{ fontSize:13, color:'#248A3D', fontWeight:600 }}>Device acknowledged the reset.</div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {!busy && !done ? (
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{
              flex:1, padding:'13px', borderRadius:14, border:'1.5px solid rgba(0,0,0,.1)',
              background:'#fff', color:'#8E8E93', fontWeight:600, fontSize:14,
              cursor:'pointer', fontFamily:'inherit',
            }}>Cancel</button>
            <button onClick={sendFactoryReset} disabled={!confirmed} style={{
              flex:1, padding:'13px', borderRadius:14, border:'none',
              background: confirmed ? 'linear-gradient(135deg,#FF3B30,#CC0000)' : 'rgba(0,0,0,.08)',
              color: confirmed ? '#fff' : '#aeaeb2', fontWeight:700, fontSize:14,
              cursor: confirmed ? 'pointer' : 'not-allowed', fontFamily:'inherit',
              boxShadow: confirmed ? '0 4px 16px rgba(255,59,48,.35)' : 'none',
            }}>Reset Now</button>
          </div>
        ) : (
          <button onClick={onClose} disabled={!done} style={{
            width:'100%', padding:'13px', borderRadius:14, border:'none',
            background: !done ? 'rgba(0,0,0,.06)'
              : isError ? 'linear-gradient(135deg,#FF3B30,#CC0000)' : 'linear-gradient(135deg,#34C759,#30D158)',
            color: !done ? '#8E8E93' : '#fff',
            fontWeight:700, fontSize:15, cursor: done ? 'pointer' : 'default',
            fontFamily:'inherit', transition:'all .4s',
            boxShadow: done ? (isError ? '0 4px 16px rgba(255,59,48,.35)' : '0 4px 16px rgba(52,199,89,.4)') : 'none',
          }}>
            {!done ? 'Waiting…' : 'Done'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── TAM config ────────────────────────────────────────────────────────────
const TAM_CONFIG = {
  '1': {
    title:    'Pump Tamper Detected',
    subtitle: 'Hardware tampering on pump unit',
    desc:     'The device has detected an unauthorized attempt to tamper with the pump hardware. This may indicate the user is trying to bypass or modify the pump mechanism.',
    color:    '#FF3B30',
    bg:       'linear-gradient(135deg,#FF3B30,#CC0000)',
    shadow:   'rgba(255,59,48,.4)',
    badge:    'PUMP TAMPER',
  },
  '2': {
    title:    'Flow Sensor Tamper Detected',
    subtitle: 'Unauthorized flow sensor modification',
    desc:     'The device has detected an unauthorized attempt to tamper with the flow sensor. The user may be trying to manipulate water flow readings or bypass the flow measurement system.',
    color:    '#FF9F0A',
    bg:       'linear-gradient(135deg,#FF9F0A,#CC7A00)',
    shadow:   'rgba(255,159,10,.4)',
    badge:    'FLOW SENSOR TAMPER',
  },
};

const TAM_HISTORY_KEY = (mac) => `hoags_tam_history_${mac}`;

const TAM_DEDUP_HOURS = 24;

function getTamHistory(mac) {
  try { return JSON.parse(localStorage.getItem(TAM_HISTORY_KEY(mac)) || '[]'); }
  catch { return []; }
}

function saveTamEvent(mac, tamCode, deviceName, extra = {}) {
  const hist = getTamHistory(mac);
  const cfg  = TAM_CONFIG[tamCode];
  if (!cfg) return false;
  const cutoff24h = Date.now() - TAM_DEDUP_HOURS * 60 * 60 * 1000;

  // Check if this tamCode already has an unacknowledged entry — overwrite it
  const existingIdx = hist.findIndex(h => h.tamCode === tamCode);
  const existing    = existingIdx >= 0 ? hist[existingIdx] : null;

  // Suppress toast+email if the existing entry is within 24h
  const suppressAlert = existing &&
    new Date(existing.detectedAt).getTime() > cutoff24h;

  const newEntry = {
    id:             Date.now(),
    tamCode,
    type:           cfg.badge,
    title:          cfg.title,
    device:         deviceName,
    mac,
    location:       extra.location || '',
    serialNo:       extra.serialNo || '',
    detectedAt:     new Date().toISOString(),
    acknowledgedAt: null,
  };

  let updated;
  if (existingIdx >= 0) {
    // Overwrite the existing entry for this tamCode (keeps only 1 per type)
    updated = [...hist];
    updated[existingIdx] = newEntry;
  } else {
    // First time this tamCode fires — add it
    updated = [newEntry, ...hist];
  }

  localStorage.setItem(TAM_HISTORY_KEY(mac), JSON.stringify(updated.slice(0, 200)));
  return !suppressAlert; // true = show toast+email, false = already alerted within 24h
}

function acknowledgeTamEvent(mac, id) {
  const hist = getTamHistory(mac);
  const idx  = hist.findIndex(h => h.id === id);
  if (idx >= 0) {
    hist[idx].acknowledgedAt = new Date().toISOString();
    localStorage.setItem(TAM_HISTORY_KEY(mac), JSON.stringify(hist));
  }
}

// ── Tamper Toast (corner notification — replaces full-page modal) ────────────
function TamperToast({ tamCode, device, mac, onAck, onClose }) {
  const cfg = TAM_CONFIG[tamCode];
  if (!cfg) return null;
  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:99999,
      width:320, borderRadius:18,
      background:'#fff',
      boxShadow:'0 12px 48px rgba(0,0,0,.22), 0 2px 8px rgba(0,0,0,.1)',
      border:`1.5px solid ${cfg.color}30`,
      overflow:'hidden',
      animation:'toastSlideIn .35s cubic-bezier(.34,1.56,.64,1)',
      fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
    }}>
      {/* Colour top bar */}
      <div style={{ height:4, background:cfg.bg }}/>
      <div style={{ padding:'14px 16px 16px', display:'flex', flexDirection:'column', gap:10 }}>
        {/* Header row */}
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{
            width:36, height:36, borderRadius:10, flexShrink:0,
            background:cfg.bg,
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:`0 3px 10px ${cfg.shadow}`,
            animation:'tamPulse 1.8s ease-in-out infinite',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 19h20L12 2Z" fill="white" fillOpacity=".3"
                stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M12 9v5" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx="12" cy="17" r="1.2" fill="white"/>
            </svg>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:800, color:'#1c1c1e', marginBottom:1 }}>
              {cfg.title}
            </div>
            <div style={{ fontSize:10, fontWeight:700, color:cfg.color, letterSpacing:.6 }}>
              {cfg.badge}
            </div>
          </div>
          <button onClick={onClose} style={{
            width:24, height:24, borderRadius:'50%', border:'none',
            background:'rgba(0,0,0,.07)', cursor:'pointer', flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:13, color:'#636366',
          }}>×</button>
        </div>
        {/* Device info */}
        <div style={{
          fontSize:12, color:'#48484a', lineHeight:1.55,
          background:'rgba(0,0,0,.025)', borderRadius:10, padding:'9px 12px',
        }}>
          <div><strong>Device:</strong> {device.friendlyname}</div>
          <div style={{ fontFamily:'monospace', fontSize:11 }}>{mac}</div>
          <div style={{ marginTop:3, color:'#8E8E93', fontSize:11 }}>
            {new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true})} · {new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
          </div>
        </div>
        {/* Actions */}
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onAck || onClose} style={{
            flex:1, padding:'9px', borderRadius:10, border:'none',
            background:cfg.bg, color:'#fff', fontWeight:700, fontSize:12,
            cursor:'pointer', fontFamily:'inherit',
            boxShadow:`0 2px 8px ${cfg.shadow}`,
          }}>Acknowledge</button>
        </div>
        <div style={{ fontSize:10, color:'#aeaeb2', textAlign:'center' }}>
          Saved to tamper history
        </div>
      </div>
      <style>{`
        @keyframes toastSlideIn {
          from { opacity:0; transform:translateX(120px) scale(.9); }
          to   { opacity:1; transform:translateX(0) scale(1); }
        }
        @keyframes tamPulse {
          0%,100% { transform:scale(1); }
          50%      { transform:scale(1.08); }
        }
      `}</style>
    </div>
  );
}

// ── Tamper History Panel ─────────────────────────────────────────────────────
function TamperHistoryPanel({ mac, deviceName, device, publish, getK2, onClose }) {
  const [history, setHistory] = useState(() => getTamHistory(mac));

  const handleAck = (id) => {
    acknowledgeTamEvent(mac, id);
    setHistory(getTamHistory(mac));
    // Publish encrypted TAM ACK to the device (same K2 flow as DS)
    sendTamAck(device?.macid || mac, publish, getK2);
  };

  const handleClear = () => {
    if (window.confirm('Clear all tamper history for this device?')) {
      localStorage.removeItem(TAM_HISTORY_KEY(mac));
      setHistory([]);
    }
  };

  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, zIndex:9999,
      background:'rgba(0,0,0,0.55)', backdropFilter:'blur(16px)',
      display:'flex', alignItems:'flex-end', justifyContent:'center',
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'#f5f5f7', borderRadius:'24px 24px 0 0',
        width:'100%', maxWidth:620, maxHeight:'80vh',
        boxShadow:'0 -20px 60px rgba(0,0,0,0.25)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
        display:'flex', flexDirection:'column',
      }}>
        {/* Handle + header */}
        <div style={{ padding:'16px 20px 12px', background:'#fff', borderRadius:'24px 24px 0 0',
          borderBottom:'1px solid rgba(0,0,0,.06)', flexShrink:0 }}>
          <div style={{ width:36, height:4, borderRadius:2, background:'#e0e0e0', margin:'0 auto 14px' }}/>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:17, fontWeight:800, color:'#1c1c1e' }}>Tamper History</div>
              <div style={{ fontSize:12, color:'#8E8E93', marginTop:2 }}>{deviceName} · {history.length} events</div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              {history.length > 0 && (
                <button onClick={handleClear} style={{
                  padding:'7px 14px', borderRadius:10, border:'none',
                  background:'rgba(255,59,48,.08)', color:'#FF3B30',
                  fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
                }}>Clear All</button>
              )}
              <button onClick={onClose} style={{
                width:30, height:30, borderRadius:'50%', border:'none',
                background:'rgba(0,0,0,.07)', cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:14, color:'#636366',
              }}>×</button>
            </div>
          </div>
        </div>

        {/* List */}
        <div style={{ overflowY:'auto', padding:'12px 16px 32px', flex:1 }}>
          {history.length === 0 ? (
            <div style={{ textAlign:'center', padding:'48px 20px', color:'#8E8E93' }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#d1d1d6"
                strokeWidth="1.5" strokeLinecap="round" style={{ margin:'0 auto 14px', display:'block' }}>
                <path d="M12 2L2 19h20L12 2Z"/><path d="M12 9v5"/><circle cx="12" cy="17" r="1"/>
              </svg>
              <p style={{ fontWeight:600, color:'#3a3a3c', fontSize:14, marginBottom:5 }}>No tamper events</p>
              <p style={{ fontSize:12 }}>Tamper alerts will appear here when detected</p>
            </div>
          ) : history.map(ev => {
            const cfg = TAM_CONFIG[ev.tamCode] || TAM_CONFIG['1'];
            const isAcknowledged = !!ev.acknowledgedAt;
            return (
              <div key={ev.id} style={{
                background:'#fff', borderRadius:14, padding:'14px 16px',
                marginBottom:10, border:`1px solid ${isAcknowledged?'rgba(0,0,0,.06)':cfg.color+'30'}`,
                boxShadow: isAcknowledged ? '0 1px 4px rgba(0,0,0,.05)' : `0 2px 12px ${cfg.shadow}22`,
                opacity: isAcknowledged ? .6 : 1,
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{
                      width:32, height:32, borderRadius:10, flexShrink:0,
                      background: isAcknowledged ? '#f0f0f0' : cfg.bg,
                      display:'flex', alignItems:'center', justifyContent:'center',
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L2 19h20L12 2Z" fill={isAcknowledged?'#8E8E93':'white'}
                          fillOpacity=".3" stroke={isAcknowledged?'#8E8E93':'white'}
                          strokeWidth="1.5" strokeLinejoin="round"/>
                        <path d="M12 9v5" stroke={isAcknowledged?'#8E8E93':'white'} strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="12" cy="17" r="1" fill={isAcknowledged?'#8E8E93':'white'}/>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>{ev.title}</div>
                      <div style={{
                        fontSize:9, fontWeight:700, padding:'1px 8px', borderRadius:20,
                        background: isAcknowledged?'rgba(0,0,0,.06)':cfg.color+'15',
                        color: isAcknowledged?'#8E8E93':cfg.color,
                        display:'inline-block', marginTop:3, letterSpacing:.5,
                      }}>{ev.type}</div>
                    </div>
                  </div>
                  {isAcknowledged ? (
                    <span style={{ fontSize:10, fontWeight:600, color:'#34C759',
                      background:'rgba(52,199,89,.08)', padding:'3px 8px', borderRadius:20 }}>Acknowledged</span>
                  ) : (
                    <button onClick={()=>handleAck(ev.id)} style={{
                      padding:'5px 12px', borderRadius:8, border:'none',
                      background:cfg.color, color:'#fff',
                      fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit', flexShrink:0,
                    }}>Acknowledge</button>
                  )}
                </div>
                <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                  <span style={{ fontSize:11, color:'#8E8E93', display:'flex', alignItems:'center', gap:4 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                    </svg>
                    {new Date(ev.detectedAt).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true})}
                  </span>
                  {ev.acknowledgedAt && (
                    <span style={{ fontSize:11, color:'#34C759' }}>
                      Ack: {new Date(ev.acknowledgedAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true})}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Device Track Status tab bar ──────────────────────────────────────────────
function TrackTabBar({ trackTab, setTrackTab }) {
  return (
    <div style={{ display:'flex', gap:0, borderBottom:'2px solid rgba(0,0,0,.06)', marginBottom:0 }}>
      {[
        { id:'recharge',    label:'Recharge History', color:'#34C759' },
        { id:'userchanges', label:'User Changes',      color:'#AF52DE' },
      ].map(t => (
        <button key={t.id} onClick={()=>setTrackTab(t.id)} style={{
          flex:1, padding:'9px 8px', border:'none', background:'transparent',
          fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
          color: trackTab===t.id ? t.color : '#8E8E93',
          borderBottom: trackTab===t.id ? `2.5px solid ${t.color}` : '2.5px solid transparent',
          marginBottom:-2,
          transition:'color .18s, border-color .18s',
        }}>{t.label}</button>
      ))}
    </div>
  );
}

export default function DeviceStatus({ device, onBack, getK2 }) {
  const mac      = sanitiseMac(device.macid);  // sanitised lowercase hex
  const macUpper = device.macid.replace(/[^a-fA-F0-9]/g, '').toUpperCase();
  const isOffline = !!device._isOffline;

  const [ds,              setDs]             = useState({});
  const [received,        setReceived]       = useState(new Set());
  const [loading,         setLoading]        = useState(true);
  const [lastFetch,       setLastFetch]      = useState(null);
  const [showDiag,        setShowDiag]       = useState(false);
  const [showFactoryReset, setShowFactoryReset] = useState(false);
  const [logs,            setLogs]           = useState([]);
  const [showRecharge,    setShowRecharge]   = useState(false);
  const [showRequest,     setShowRequest]    = useState(false);
  const [showErrorStack,  setShowErrorStack] = useState(false);
  const [activeK2,        setActiveK2]       = useState(null);
  const [tamAlert,        setTamAlert]       = useState(null);
  const [showTamHistory,  setShowTamHistory] = useState(false);
  const [tamUnacked,      setTamUnacked]     = useState(0);
  const [rechargeHistory, setRechargeHistory]= useState([]);  // DynamoDB recharge log
  const [userChanges,     setUserChanges]    = useState([]);  // DynamoDB user/DS change log
  const [rcHistLoading,   setRcHistLoading]  = useState(false);
  const [trackTab,        setTrackTab]       = useState('recharge');
  const lastTamRef = useRef(null);

  const unsubRef    = useRef(null);
  const timerRef    = useRef(null);
  const receivedRef = useRef(new Set());

  const { connected, subscribe, publish } = useMqttProxy();

  const addLog = useCallback((type, msg) => {
    console.log(`[DS][${type}]`, msg);
    setLogs(prev => [...prev.slice(-299), { type, msg, time: nowStr() }]);
  }, []);

  const requestStatus = useCallback(() => {
    if (!connected) { addLog('err', 'MQTT not connected.'); return; }

    setLoading(true);
    setDs({});
    const fresh = new Set();
    setReceived(fresh);
    receivedRef.current = fresh;

    const cmdT  = CMD_TOPIC(mac);
    const respT = RESP_TOPIC(mac);
    const dsRequest = { IM: mac, CMD: 'DS' };

    addLog('info',     `Device MAC: ${mac}`);
    addLog('resp_sub', `Subscribing  → ${respT}`);
    addLog('cmd_pub',  `Publishing   → ${cmdT}`);

    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    clearTimeout(timerRef.current);

    // ══════════════════════════════════════════════════════════════
    // FULL FLOW (same as Python script):
    //   1. Handshake → get encrypted K2 from Lambda
    //   2. Derive K1 locally → decrypt K2
    //   3. Encrypt DS payload with K2 → publish RAW hex
    //   4. Listen on response topic → decrypt VAL with K2 → update UI
    // ══════════════════════════════════════════════════════════════

    // Step 1+2: Get K2 (try existing, otherwise do fresh handshake)
    let k2 = getK2 ? getK2(device.macid) : null;

    const doEncryptAndSend = async (activeK2) => {
      // Step 3: Encrypt DS request — same as Python encrypt_ds_payload()
      const dsPayload = JSON.stringify({IM: mac, CMD: 'DS'});
      const plainHex = stringToHex(dsPayload);
      addLog('cmd_pub', `📄 Plain JSON: ${dsPayload}`);
      addLog('cmd_pub', `🔡 Plain HEX: ${plainHex}`);

      const encrypted = await aesEncrypt(plainHex, activeK2);
      addLog('cmd_pub', `🔐 Encrypted: ${encrypted.substring(0, 40)}... (${encrypted.length} chars)`);

      // Step 4: Publish RAW encrypted hex only (NOT JSON wrapped)
      addLog('cmd_pub', `📤 Publishing raw encrypted hex to ${cmdT}`);
      publish(cmdT, encrypted);
    };

    // Subscribe to response topic FIRST (like Python)
    const unsub = subscribe(respT, async (message) => {
      const raw = typeof message === 'string' ? message : JSON.stringify(message);
      addLog('pkt_rx', `↓ Raw: ${raw.substring(0, 100)}...`);

      try {
        let json;
        const parsed = typeof message === 'string' ? JSON.parse(message) : message;

        // Check if response has encrypted VAL field (device sends {"VAL":"hex..."})
        // Only decrypt if VAL is long hex (>20 chars) — short VAL like "1","2" is plaintext tamper code
        if (parsed.VAL && /^[0-9a-fA-F]+$/.test(parsed.VAL) && parsed.VAL.length > 20 && k2) {
          addLog('pkt_rx', `🔓 Decrypting VAL (${parsed.VAL.length} hex chars)`);
          const decHex = await aesDecrypt(parsed.VAL, k2);
          const decStr = hexToString(decHex);
          addLog('pkt_rx', `🔓 Decrypted: ${decStr.substring(0, 120)}`);
          json = JSON.parse(decStr);
        } else {
          // Plaintext or no K2
          json = parsed;
        }

        addLog('pkt_rx', `  IM=${json.IM}  CMD=${json.CMD}  Q=${json.Q}  S=${json.S}`);

        // Handle TAM (tamper) packets — show toast + send email
        if (json.CMD === 'TAM') {
          addLog('pkt_rx', `⚠️ TAMPER detected: VAL=${json.VAL}`);
          const tamCode = String(json.VAL);
          if (TAM_CONFIG[tamCode] && lastTamRef.current !== tamCode) {
            lastTamRef.current = tamCode;
            setTimeout(() => { lastTamRef.current = null; }, 20000);
            const isNew = saveTamEvent(mac, tamCode, device.friendlyname);
            if (isNew) {
              // New event within 24h window — show toast + send email
              setTamAlert(tamCode);
              setTamUnacked(getTamHistory(mac).filter(h=>!h.acknowledgedAt).length);
              fetch(`${API_BASE}/api/tamper-alert`, {
                method:'POST',
                headers:{'Content-Type':'application/json'},
                body: JSON.stringify({
                  tamCode,
                  deviceName: device.friendlyname,
                  mac,
                  tamType: TAM_CONFIG[tamCode]?.badge,
                  detectedAt: new Date().toISOString(),
                  customerName: device._registration?.userName || '',
                  phone: device._registration?.phone || '',
                  location: device._registration?.location || '',
                }),
              }).catch(e => console.error('[TAM] email alert failed:', e));
            } else {
              addLog('info', `⏸ TAM [${tamCode}] suppressed — duplicate within 24h`);
            }
          }
          return;
        }

        if (json.CMD !== 'DS') {
          addLog('pkt_skip', `Skipped — CMD="${json.CMD}" expected "DS"`);
          return;
        }

        const qNum = json.Q ? parseInt(json.Q.split('/')[0]) : 0;
        addLog('pkt_rx', `✅ Packet Q${qNum}/4 accepted — merging`);

        setDs(prev => ({ ...prev, ...json }));
        setReceived(prev => {
          const next = new Set(prev);
          next.add(qNum);
          receivedRef.current = next;
          addLog('pkt_rx', `   Progress: ${[...next].sort().join(', ')} / 4`);
          if (next.size >= 4) {
            setLoading(false);
            setLastFetch(new Date());
            clearTimeout(timerRef.current);
            addLog('info', '🎉 All 4 packets received — status complete!');
          }
          return next;
        });
      } catch (e) {
        addLog('err', `Parse error: ${e.message}`);
      }
    });

    unsubRef.current = unsub;

    // Now do the handshake + encrypt + publish (async)
    (async () => {
      try {
        // If no K2 yet, do fresh handshake (same as Python's NK request flow)
        if (!k2) {
          addLog('info', `🤝 No K2 — starting handshake for ${macUpper}`);

          // Step 1a: Derive K1 locally
          const k1 = await generateK1(macUpper);
          addLog('info', `🔑 K1 derived: ${k1.substring(0, 16)}...`);

          // Step 1b: Call handshake API → Lambda → get encrypted K2 (KY)
          addLog('info', `📡 Requesting K2 from Lambda...`);
          const res = await fetch(`${API_BASE}/api/handshake`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
            body: JSON.stringify({ macId: macUpper, payload: { IM: macUpper, KY: '', CMD: 'HDS' } }),
          });

          if (!res.ok) throw new Error(`Handshake API ${res.status}`);
          const data = await res.json();
          if (!data.success || !data.KY) throw new Error(data.error || 'No KY in response');

          addLog('info', `📥 Encrypted K2 received: ${data.KY.substring(0, 30)}...`);

          // Step 2: Decrypt K2 with K1
          const k2Hex = await aesDecrypt(data.KY, k1);
          addLog('info', `✅ K2 decrypted: ${k2Hex.substring(0, 16)}...`);
          k2 = k2Hex;
          setActiveK2(k2Hex);
          // Cache globally so TAM ACK can reuse this K2 without re-handshaking
          window.__hoagsK2 = window.__hoagsK2 || {};
          window.__hoagsK2[macUpper.toLowerCase()] = k2Hex;
        } else {
          addLog('info', `🔑 Using existing K2: ${k2.substring(0, 16)}...`);
          setActiveK2(k2);
        }

        // Step 3+4: Encrypt DS and publish
        await doEncryptAndSend(k2);

      } catch (err) {
        addLog('err', `❌ ${err.message}`);
        // Fallback: send plaintext DS
        addLog('cmd_pub', `⚠️ Fallback: sending plaintext DS`);
        publish(cmdT, JSON.stringify({ IM: mac, CMD: 'DS' }));
      }
    })();

    const autoRetryRef = { count: 0 };
    timerRef.current = setTimeout(() => {
      const got = receivedRef.current;
      addLog('timeout', `⏱ ${DS_TIMEOUT/1000}s timeout — got ${got.size}/4 packets [${[...got].sort().join(',')}]`);
      if (got.size === 0) {
        addLog('timeout', 'No packets received. Verify device is online and MAC is correct.');
        setLoading(false);
        setLastFetch(new Date());
        if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
      } else if (got.size < 4 && autoRetryRef.count === 0) {
        // Auto-retry: got some packets but not all — re-send DS once
        autoRetryRef.count++;
        addLog('info', `🔄 Got ${got.size}/4 packets — auto-retrying DS request...`);
        if (k2) {
          doEncryptAndSend(k2).catch(() => publish(cmdT, JSON.stringify(dsRequest)));
        } else {
          publish(cmdT, JSON.stringify(dsRequest));
        }
        // Final timeout after another 15s
        timerRef.current = setTimeout(() => {
          const got2 = receivedRef.current;
          addLog('timeout', `⏱ Final timeout — got ${got2.size}/4 packets`);
          setLoading(false);
          setLastFetch(new Date());
          if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
        }, 15000);
      } else {
        setLoading(false);
        setLastFetch(new Date());
        if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
      }
    }, DS_TIMEOUT);
  }, [connected, mac, subscribe, publish, addLog]);

  useEffect(() => {
    if (isOffline) {
      // Device is offline — fetch last known status from API
      (async () => {
        try {
          addLog('info', `📡 Device offline — fetching last status from API...`);
          const res = await fetch(`${LAST_STATUS_API}?macid=${mac}`);
          if (!res.ok) throw new Error(`API ${res.status}`);
          const json = await res.json();

          // API format: { latest_recharge: {...}, response_logs: [{data:{...}}, ...] }
          const logs = json.response_logs || json.records || [];
          if (logs.length > 0) {
            const merged = {};
            const qNums = new Set();
            logs.forEach(rec => {
              const d = rec.data || rec;
              Object.assign(merged, d);
              if (d.Q) {
                const qNum = parseInt(d.Q.split('/')[0]);
                if (qNum) qNums.add(qNum);
              }
            });
            merged._fromApi = true;

            // Merge latest recharge info if available
            if (json.latest_recharge && json.latest_recharge.data) {
              const rc = json.latest_recharge;
              merged._recharge = {
                version: rc.version || rc.data?.V,
                netLiters: rc.net_liters || rc.data?.NL,
                extra: rc.extra || rc.data?.X,
                time: rc.data?.T,
                updatedAt: rc.updated_at,
              };
            }

            // Merge registration/customer info if available
            if (json.registration) {
              const reg = json.registration;
              merged._registration = {
                userName: reg.Enduser_name || reg.cust_name || '',
                phone: reg.phone_no || '',
                location: reg.location || '',
                dob: reg.dob || '',
                macId: reg.mac_id || '',
                prodType: reg.prod_type || '',
                modelNo: reg.model_no || '',
                friendlyName: reg.friendly_name || '',
                custName: reg.cust_name === 'Livpure' ? 'Havells' : (reg.cust_name || ''),
                versionNo: reg.version_no || '',
              };
            }

            addLog('info', `✅ Got ${logs.length} records from API — merged into status`);
            if (merged._recharge) {
              addLog('info', `💳 Last recharge: ${merged._recharge.netLiters}L, validity: ${merged._recharge.version}`);
            }
            setDs(merged);
            setReceived(qNums);
            setLastFetch(new Date());
          } else {
            addLog('info', `⚠️ No stored status found for ${mac}`);
          }
        } catch (err) {
          addLog('err', `❌ Failed to fetch last status: ${err.message}`);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }
    // Device is online — fetch registration data from API + do MQTT
    // Fetch registration info (customer profile) in background
    (async () => {
      try {
        const res = await fetch(`${LAST_STATUS_API}?macid=${mac}`);
        if (res.ok) {
          const json = await res.json();
          if (json.registration) {
            const reg = json.registration;
            setDs(prev => ({
              ...prev,
              _registration: {
                userName: reg.Enduser_name || reg.cust_name || '',
                phone: reg.phone_no || '',
                location: reg.location || '',
                dob: reg.dob || '',
                macId: reg.mac_id || '',
                prodType: reg.prod_type || '',
                modelNo: reg.model_no || '',
                friendlyName: reg.friendly_name || '',
                custName: reg.cust_name === 'Livpure' ? 'Havells' : (reg.cust_name || ''),
                versionNo: reg.version_no || '',
              },
            }));
          }
          if (json.latest_recharge && json.latest_recharge.data) {
            const rc = json.latest_recharge;
            setDs(prev => ({
              ...prev,
              _recharge: {
                version: rc.version || rc.data?.V,
                netLiters: rc.net_liters || rc.data?.NL,
                extra: rc.extra || rc.data?.X,
                time: rc.data?.T,
                updatedAt: rc.updated_at,
              },
            }));
          }
        }
      } catch (e) { /* ignore — registration data is optional */ }
    })();
    // Do normal MQTT handshake + encrypted DS
    if (connected) requestStatus();
    return () => {
      clearTimeout(timerRef.current);
      if (unsubRef.current) unsubRef.current();
    };
  }, [connected]); // eslint-disable-line

  const temp    = parseTemp(ds.TMP);
  const wq      = ds.WQ;
  const hasData = received.size > 0;

  // Init unacked count on mount
  useEffect(() => {
    setTamUnacked(getTamHistory(mac).filter(h=>!h.acknowledgedAt).length);
  }, [mac]); // eslint-disable-line

  // Fetch recharge history + user changes from DynamoDB via Lambda API
  useEffect(() => {
    const rawMac = device.macid.replace(/[^a-fA-F0-9]/g, '').toLowerCase();
    setRcHistLoading(true);
    // Fetch recharge records
    fetch(`https://i4hcblb998.execute-api.ap-south-1.amazonaws.com/default/latest_device_status_track?macid=${rawMac}&type=rc&all=true`)
      .then(r => r.json())
      .then(data => { if (data && Array.isArray(data.records)) setRechargeHistory(data.records); })
      .catch(() => {})
      .finally(() => setRcHistLoading(false));
    // Fetch user/status change records (DS type)
    fetch(`https://i4hcblb998.execute-api.ap-south-1.amazonaws.com/default/latest_device_status_track?macid=${rawMac}&type=ds&all=true`)
      .then(r => r.json())
      .then(data => { if (data && Array.isArray(data.records)) setUserChanges(data.records); })
      .catch(() => {});
  }, [device.macid]); // eslint-disable-line

  return (
    <div style={{
      fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",
      maxWidth:860, margin:'0 auto',
    }}>
      <style>{STYLES}</style>

      {/* ══ Top bar ══ */}
      <div className="ds-topbar" style={{
        display:'flex', alignItems:'center', gap:12, marginBottom:22,
      }}>
        <button onClick={onBack} style={{
          width:38, height:38, borderRadius:'50%',
          background:'rgba(0,0,0,.06)', border:'none', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
          flexShrink:0, transition:'background .15s',
        }}>
          <Icons.Back color="#007AFF" size={18}/>
        </button>

        {/* Purifier product image — left of device name */}
        <WaterPurifierIcon size={48}/>

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:20, fontWeight:800, color:'#1c1c1e', letterSpacing:-.4, lineHeight:1.1 }}>
            {device.friendlyname}
          </div>
          <div style={{ fontSize:11, color:'#8E8E93', marginTop:4, fontFamily:'"SF Mono",monospace' }}>
            {mac}
          </div>
        </div>

        {/* Havells logo — right side */}
        <img src="/havells-logo.png" alt="Havells" style={{ height:36, objectFit:'contain', flexShrink:0 }}/>

        <button onClick={requestStatus} disabled={loading || !connected} style={{
          display:'inline-flex', alignItems:'center', gap:7,
          padding:'9px 18px', borderRadius:12, border:'none',
          background: loading||!connected ? '#c7c7cc' : 'linear-gradient(135deg,#007AFF,#0056d3)',
          color:'#fff', fontWeight:700, fontSize:13, cursor: loading||!connected ? 'not-allowed':'pointer',
          boxShadow: loading||!connected ? 'none' : '0 4px 16px rgba(0,122,255,.35)',
          fontFamily:'inherit', transition:'all .2s', flexShrink:0,
        }}>
          <span style={loading ? { animation:'spinAnim .8s linear infinite', display:'flex' } : {}}>
            <Icons.Refresh color="#fff" size={14}/>
          </span>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* ══ Hero status card ══ */}
      <div style={{
        background:'linear-gradient(135deg,#0f172a 0%,#1e3a5f 60%,#0f172a 100%)',
        borderRadius:22, padding:'20px 22px', marginBottom:18,
        boxShadow:'0 8px 32px rgba(0,0,0,.25)',
        display:'flex', flexDirection:'column', gap:14,
      }}>
        {/* status row */}
        <div className="ds-hero" style={{
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {loading
              ? <div style={{ width:24, height:24, borderRadius:'50%', border:'3px solid rgba(96,170,255,.3)', borderTopColor:'#60aaff', animation:'spinAnim .7s linear infinite' }}/>
              : hasData
                ? <Icons.Check color="#34C759" size={24}/>
                : <Icons.Alert color="#FF9F0A" size={24}/>
            }
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:'#e6edf3' }}>
                {loading ? 'Fetching device status…' : hasData ? 'Status received' : 'No data — check debug log'}
              </div>
              {lastFetch && (
                <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', marginTop:2 }}>
                  Last updated {lastFetch.toLocaleTimeString('en-GB', { hour12:false })}
                </div>
              )}
            </div>
          </div>
          <PacketDots received={received}/>
        </div>


      </div>

      {/* ══ TAM Alert Banner ══ */}
      {hasData && ds.TAM && ds.TAM !== '0' && TAM_CONFIG[ds.TAM] && (() => {
        const cfg = TAM_CONFIG[ds.TAM];
        return (
          <div style={{
            background:`linear-gradient(135deg,${cfg.color}12,${cfg.color}06)`,
            border:`1.5px solid ${cfg.color}35`,
            borderRadius:16, padding:'14px 18px', marginBottom:16,
            display:'flex', alignItems:'center', gap:14,
            animation:'fadeUp .3s ease',
          }}>
            <div style={{
              width:40,height:40,borderRadius:12,flexShrink:0,
              background:cfg.bg,
              display:'flex',alignItems:'center',justifyContent:'center',
              boxShadow:`0 4px 14px ${cfg.shadow}`,
              animation:'tamPulse 1.8s ease-in-out infinite',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 19h20L12 2Z" fill="white" fillOpacity=".3" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M12 9v5" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                <circle cx="12" cy="17" r="1.2" fill="white"/>
              </svg>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:800, color:cfg.color, marginBottom:2 }}>{cfg.title}</div>
              <div style={{ fontSize:11, color:'#48484a' }}>{cfg.subtitle}</div>
            </div>
            <button onClick={()=>setShowTamHistory(true)} style={{
              padding:'7px 14px', borderRadius:10, border:`1px solid ${cfg.color}30`,
              background:'#fff', color:cfg.color, fontSize:12, fontWeight:700,
              cursor:'pointer', fontFamily:'inherit', flexShrink:0,
            }}>View History</button>
          </div>
        );
      })()}

      {/* ══ Debug panel removed ══ */}


      {/* ══ Empty state ══ */}
      {!hasData && !loading && (
        <div style={{
          textAlign:'center', padding:'60px 24px',
          background:'#fff', borderRadius:20,
          border:'1px solid rgba(0,0,0,.06)',
          boxShadow:'0 2px 16px rgba(0,0,0,.07)',
          marginBottom:16,
        }}>
          <Icons.Signal color="#C7C7CC" size={48}/>
          <div style={{ fontSize:16, fontWeight:700, color:'#1c1c1e', marginTop:16 }}>
            No status data received
          </div>
          <div style={{ fontSize:13, color:'#8E8E93', marginTop:6 }}>
            Check the debug log above for details
          </div>
        </div>
      )}

      {hasData && (<>

        {/* ══ Customer Profile + Recharge History — side by side ══ */}
        <div style={{ display:'grid', gridTemplateColumns: ds._registration ? '1fr 1fr' : '1fr', gap:14, marginBottom:16, alignItems:'start' }}>

          {/* LEFT — Customer Profile */}
          {ds._registration && (
            <div style={{
              background:'#fff', borderRadius:18, padding:'18px 20px',
              border:'1px solid rgba(0,0,0,.06)', boxShadow:'0 2px 12px rgba(0,0,0,.06)',
              height:'100%', boxSizing:'border-box',
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <div style={{
                  width:42, height:42, borderRadius:13,
                  background:'linear-gradient(135deg,#DC2626,#EF4444)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:'#fff', fontSize:17, fontWeight:800, flexShrink:0,
                }}>
                  {(ds._registration.userName || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#1c1c1e' }}>
                    {ds._registration.userName || 'Unknown'}
                  </div>
                  <div style={{ fontSize:11, color:'#8E8E93' }}>Registered Customer</div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:7 }}>
                {[
                  ['📞 Phone', ds._registration.phone],
                  ['📍 Location', ds._registration.location],
                  ['🎂 DOB', ds._registration.dob],
                  ['📱 Device', ds._registration.friendlyName],
                  ['🔧 Model', ds._registration.modelNo],
                  ['🏭 Brand', ds._registration.custName],
                ].filter(([,v]) => v).map(([label, value]) => (
                  <div key={label} style={{ padding:'9px 12px', background:'rgba(0,0,0,.025)', borderRadius:11 }}>
                    <div style={{ fontSize:9, color:'#8E8E93', fontWeight:600, letterSpacing:.3, marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:12, color:'#1c1c1e', fontWeight:600 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RIGHT — Device Track Status (Recharge History + User Changes) */}
          <div style={{
            background:'#fff', borderRadius:18,
            border:'1px solid rgba(0,0,0,.06)', boxShadow:'0 2px 12px rgba(0,0,0,.06)',
            overflow:'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding:'14px 18px 0',
              borderBottom:'1px solid rgba(0,0,0,.05)',
            }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                  <div style={{
                    width:32, height:32, borderRadius:10,
                    background:'linear-gradient(135deg,#007AFF,#0056d3)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:16,
                  }}>📊</div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:'#1c1c1e' }}>Device Track Status</div>
                    <div style={{ fontSize:11, color:'#8E8E93' }}>
                      {rcHistLoading ? 'Loading…' : `${rechargeHistory.length} records from DynamoDB`}
                    </div>
                  </div>
                </div>
                {rechargeHistory.length > 0 && (
                  <span style={{
                    fontSize:11, fontWeight:700, padding:'2px 9px', borderRadius:20,
                    background:'rgba(0,122,255,.1)', color:'#007AFF',
                  }}>{rechargeHistory.length}</span>
                )}
              </div>
              {/* Tab bar */}
              <TrackTabBar trackTab={trackTab} setTrackTab={setTrackTab}/>
            </div>

            {/* List */}
            <div style={{ maxHeight:280, overflowY:'auto' }}>
              {rcHistLoading ? (
                <div style={{ padding:'24px', textAlign:'center' }}>
                  <div style={{ width:20, height:20, borderRadius:'50%', border:'2.5px solid rgba(0,122,255,.3)', borderTopColor:'#007AFF', animation:'spinAnim .7s linear infinite', margin:'0 auto 8px' }}/>
                  <div style={{ fontSize:12, color:'#8E8E93' }}>Fetching from DynamoDB…</div>
                </div>
              ) : trackTab === 'recharge' ? (
                rechargeHistory.length === 0 ? (
                  <div style={{ padding:'28px 20px', textAlign:'center', color:'#8E8E93' }}>
                    <div style={{ fontSize:24, marginBottom:8 }}>📭</div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#3a3a3c' }}>No recharge records</div>
                    <div style={{ fontSize:11, marginTop:4 }}>Recharge history will appear here</div>
                  </div>
                ) : rechargeHistory.map((rec, idx) => {
                  const d = rec.data || {};
                  const ts = rec.timestamp ? new Date(rec.timestamp) : null;
                  const isLatest = idx === 0;
                  return (
                    <div key={idx} style={{
                      display:'flex', alignItems:'center', gap:12,
                      padding:'11px 16px',
                      borderBottom: idx < rechargeHistory.length - 1 ? '1px solid rgba(0,0,0,.04)' : 'none',
                      background: isLatest ? 'rgba(52,199,89,.04)' : 'transparent',
                    }}>
                      <div style={{
                        width:28, height:28, borderRadius:'50%', flexShrink:0,
                        background: isLatest ? 'linear-gradient(135deg,#34C759,#248A3D)' : 'rgba(0,0,0,.06)',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:10, fontWeight:800,
                        color: isLatest ? '#fff' : '#8E8E93',
                      }}>{isLatest ? '★' : rechargeHistory.length - idx}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                          <span style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>💧 {d.NL || '—'} L</span>
                          {d.V && (
                            <span style={{
                              fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:20,
                              background:'rgba(0,122,255,.08)', color:'#007AFF',
                            }}>Validity: {d.V}</span>
                          )}
                          {isLatest && (
                            <span style={{
                              fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:20,
                              background:'rgba(52,199,89,.12)', color:'#34C759', letterSpacing:.4,
                            }}>LATEST</span>
                          )}
                        </div>
                        <div style={{ display:'flex', gap:10, marginTop:3, flexWrap:'wrap' }}>
                          {d.X && <span style={{ fontSize:10, color:'#8E8E93', fontFamily:'monospace' }}>Txn: {d.X}</span>}
                          {ts && <span style={{ fontSize:10, color:'#aeaeb2' }}>{ts.toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true})}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                /* User Changes tab — Tamper history + Filter changes + DS status changes */
                (() => {
                  // Merge: tamper history from localStorage + hardcoded filter changes + DS records
                  const tamHist = getTamHistory(mac).map(ev => ({
                    _type: 'tamper',
                    timestamp: new Date(ev.detectedAt),
                    tamCode: ev.tamCode,
                    title: ev.title,
                    badge: ev.type,
                    color: ev.tamCode==='1'?'#FF3B30':'#FF9F0A',
                    acked: !!ev.acknowledgedAt,
                    ackedAt: ev.acknowledgedAt,
                  }));

                  // Hardcoded filter change records (replace with real API data when available)
                  const FILTER_PARTS = [
                    { name:'RO Membrane',    partNo:'HVL-RO-7040',  emoji:'💧', color:'#0ea5e9' },
                    { name:'Carbon Block',   partNo:'HVL-CB-10IN',  emoji:'🪒', color:'#6B7280' },
                    { name:'Sediment',       partNo:'HVL-SD-5MIC',  emoji:'🌫️', color:'#A78BFA' },
                    { name:'Alkaline',       partNo:'HVL-ALK-400',  emoji:'⛓️',  color:'#10b981' },
                    { name:'Mineral Cart.',  partNo:'HVL-MIN-300',  emoji:'🧬', color:'#f59e0b' },
                    { name:'Copper Cart.',   partNo:'HVL-COP-200',  emoji:'🔶', color:'#b45309' },
                  ];
                  // Build filter change events from rechargeHistory (simulate: each recharge → filter reset)
                  // In production these would come from a real API
                  const filterChanges = rechargeHistory.slice(0,2).flatMap((rec, ri) =>
                    FILTER_PARTS.slice(0, ri===0?2:1).map(f => ({
                      _type: 'filter',
                      timestamp: rec.timestamp ? new Date(rec.timestamp) : new Date(Date.now() - ri*86400000*30),
                      filterName: f.name,
                      partNo: f.partNo,
                      emoji: f.emoji,
                      color: f.color,
                    }))
                  );

                  // DS records
                  const dsRecs = userChanges.map(rec => ({
                    _type: 'ds',
                    timestamp: rec.timestamp ? new Date(rec.timestamp) : new Date(0),
                    data: rec.data || {},
                  }));

                  // Combine & sort newest first
                  const combined = [...tamHist, ...filterChanges, ...dsRecs]
                    .sort((a,b) => b.timestamp - a.timestamp);

                  return combined.length === 0 ? (
                    <div style={{ padding:'28px 20px', textAlign:'center', color:'#8E8E93' }}>
                      <div style={{ fontSize:24, marginBottom:8 }}>📋</div>
                      <div style={{ fontSize:13, fontWeight:600, color:'#3a3a3c' }}>No user change records</div>
                      <div style={{ fontSize:11, marginTop:4 }}>Device changes will appear here</div>
                    </div>
                  ) : combined.map((item, idx) => (
                    <div key={idx} style={{
                      display:'flex', alignItems:'flex-start', gap:10,
                      padding:'11px 14px',
                      borderBottom: idx < combined.length-1 ? '1px solid rgba(0,0,0,.04)' : 'none',
                      background: idx===0 ? 'rgba(0,0,0,.02)' : 'transparent',
                    }}>
                      {/* Icon */}
                      <div style={{
                        width:30, height:30, borderRadius:9, flexShrink:0, marginTop:1,
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:14,
                        background: item._type==='tamper'
                          ? (item.tamCode==='1'?'rgba(255,59,48,.12)':'rgba(255,159,10,.12)')
                          : item._type==='filter'
                            ? `${item.color}18`
                            : 'rgba(175,82,222,.1)',
                      }}>
                        {item._type==='tamper' ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2L2 19h20L12 2Z" fill={item.color} fillOpacity=".4"
                              stroke={item.color} strokeWidth="1.5" strokeLinejoin="round"/>
                            <path d="M12 9v5" stroke={item.color} strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="12" cy="17" r="1" fill={item.color}/>
                          </svg>
                        ) : item._type==='filter' ? (
                          <span style={{ fontSize:13 }}>{item.emoji}</span>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"
                              stroke="#AF52DE" strokeWidth="1.8" strokeLinecap="round"/>
                          </svg>
                        )}
                      </div>

                      {/* Content */}
                      <div style={{ flex:1, minWidth:0 }}>
                        {item._type==='tamper' && (
                          <>
                            <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:3 }}>
                              <span style={{ fontSize:12, fontWeight:700, color:item.color }}>{item.title}</span>
                              <span style={{ fontSize:9, fontWeight:800, padding:'1px 6px', borderRadius:20,
                                background:`${item.color}18`, color:item.color, letterSpacing:.5 }}>{item.badge}</span>
                              {item.acked && <span style={{ fontSize:9, fontWeight:700, color:'#34C759',
                                background:'rgba(52,199,89,.1)', padding:'1px 6px', borderRadius:20 }}>✓ Ack</span>}
                            </div>
                            <div style={{ fontSize:10, color:'#8E8E93' }}>
                              {item.timestamp.toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true})}
                              {item.ackedAt && <span style={{ color:'#34C759', marginLeft:8 }}>Ack: {new Date(item.ackedAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true})}</span>}
                            </div>
                          </>
                        )}
                        {item._type==='filter' && (
                          <>
                            <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:3 }}>
                              <span style={{ fontSize:12, fontWeight:700, color:'#1c1c1e' }}>{item.filterName} Replaced</span>
                              <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:20,
                                background:`${item.color}18`, color:item.color }}>FILTER CHANGE</span>
                            </div>
                            <div style={{ fontSize:10, color:'#8E8E93', marginBottom:2 }}>
                              Part No: <span style={{ fontFamily:'monospace', color:'#48484a', fontWeight:600 }}>{item.partNo}</span>
                            </div>
                            <div style={{ fontSize:10, color:'#aeaeb2' }}>
                              {item.timestamp.toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true})}
                            </div>
                          </>
                        )}
                        {item._type==='ds' && (
                          <>
                            <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:3 }}>
                              <span style={{ fontSize:12, fontWeight:700, color:'#1c1c1e' }}>Status Update</span>
                              {item.data.CMD && <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:20,
                                background:'rgba(175,82,222,.1)', color:'#AF52DE' }}>{item.data.CMD}</span>}
                              {item.data.TAM && item.data.TAM!=='0' && <span style={{ fontSize:9, fontWeight:700,
                                color:'#FF3B30', background:'rgba(255,59,48,.1)', padding:'1px 6px', borderRadius:20 }}>⚠️ TAM:{item.data.TAM}</span>}
                            </div>
                            <div style={{ display:'flex', gap:8, flexWrap:'wrap', fontSize:10, color:'#8E8E93', marginBottom:2 }}>
                              {item.data.PM  && <span>Pump: {item.data.PM==='2'?'ON':'OFF'}</span>}
                              {item.data.TK  && <span>Tank: {({1:'Low',2:'Med',3:'High',4:'Low→Med'}[item.data.TK]||item.data.TK)}</span>}
                              {item.data.FW  && <span>FW: {item.data.FW}</span>}
                            </div>
                            <div style={{ fontSize:10, color:'#aeaeb2' }}>
                              {item.timestamp.toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true})}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ));
                })()
              )}
            </div>
          </div>
        </div>

        {/* ══ No Plan Banner — show only when V=000 or TL=0 ══ */}
        {ds.V === '000' && (
          <div style={{
            background:'linear-gradient(135deg,rgba(255,59,48,.08),rgba(255,149,0,.06))',
            border:'1.5px solid rgba(255,59,48,.2)',
            borderRadius:18, padding:'18px 20px', marginBottom:18,
            display:'flex', alignItems:'center', gap:16,
            animation:'fadeUp .4s ease',
          }}>
            <div style={{
              width:48, height:48, borderRadius:14, flexShrink:0,
              background:'linear-gradient(135deg,#FF3B30,#FF9F0A)',
              display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 4px 14px rgba(255,59,48,.35)',
              fontSize:22,
            }}>💳</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:15, fontWeight:800, color:'#FF3B30', letterSpacing:-.3, marginBottom:3 }}>
                No Active Plan
              </div>
              <div style={{ fontSize:13, color:'#8E8E93', lineHeight:1.5 }}>
                No active plan assigned to this device.
                Recharge to restore purifier access.
              </div>
            </div>
            <button
              onClick={() => setShowRecharge(true)}
              style={{
                padding:'10px 18px', borderRadius:12, border:'none',
                background:'linear-gradient(135deg,#FF3B30,#FF9F0A)',
                color:'#fff', fontWeight:700, fontSize:13,
                cursor:'pointer', fontFamily:'inherit', flexShrink:0,
                boxShadow:'0 4px 14px rgba(255,59,48,.35)',
              }}>Recharge Now</button>
          </div>
        )}

        {/* ══════════════════════════════════════════
            SECTION 1 — Purify Mode (animated) + Tank Level (animated)
        ══════════════════════════════════════════ */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

        {/* LEFT — Purify Mode animated card — always full colour */}
          <div style={{
            background: 'linear-gradient(160deg,#f0fff4,#fff)',
            borderRadius:20, padding:'28px 20px',
            boxShadow:'0 4px 24px rgba(0,0,0,.08)',
            border: '1.5px solid rgba(52,199,89,.25)',
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            minHeight:240, position:'relative', overflow:'hidden',
          }}>
            <div style={{ position:'absolute',top:0,left:0,right:0,height:4,
              background: 'linear-gradient(90deg,#34C759,#30D158)',
              borderRadius:'20px 20px 0 0' }}/>

            <div style={{ fontSize:10, color:'#8E8E93', fontWeight:700, letterSpacing:1,
              marginBottom:20, textAlign:'center' }}>PURIFY MODE</div>

            {/* Large animated pump — always spinning/active colour */}
            <div style={{ position:'relative', width:140, height:140, marginBottom:20 }}>
              {/* Pulse rings — green when active, gray when off */}
              {[140,112,84].map((sz,i) => (
                <div key={i} style={{
                  position:'absolute',
                  top:'50%', left:'50%',
                  width:sz, height:sz,
                  marginTop:-sz/2, marginLeft:-sz/2,
                  borderRadius:'50%',
                  border: ds.PM==='2' ? '2px solid #34C759' : '2px solid #c7c7cc',
                  animation:`pumpRing 2.4s ease-out ${i*0.5}s infinite`,
                }}/>
              ))}
              {/* Outer glow ring */}
              <div style={{
                position:'absolute', top:'50%', left:'50%',
                width:100, height:100, marginTop:-50, marginLeft:-50,
                borderRadius:'50%',
                background: ds.PM==='2'
                  ? 'radial-gradient(circle,rgba(52,199,89,.25) 0%,rgba(52,199,89,0) 70%)'
                  : 'radial-gradient(circle,rgba(142,142,147,.15) 0%,rgba(142,142,147,0) 70%)',
              }}/>
              {/* Main spinning circle — gray when off, green when active */}
              <div style={{
                position:'absolute', top:'50%', left:'50%',
                width:80, height:80, marginTop:-40, marginLeft:-40,
                borderRadius:'50%',
                background: ds.PM==='2'
                  ? 'linear-gradient(135deg,#34C759,#30D158)'
                  : 'linear-gradient(135deg,#d1d1d6,#c7c7cc)',
                boxShadow: ds.PM==='2'
                  ? '0 8px 32px rgba(52,199,89,.5)'
                  : '0 4px 16px rgba(0,0,0,.12)',
                display:'flex', alignItems:'center', justifyContent:'center',
                animation: 'pumpSpin 3s linear infinite',
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="2"/>
                  <path d="M12 2v3M12 19v3M2 12h3M19 12h3"
                    stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M5.6 5.6l2 2M16.4 16.4l2 2M5.6 18.4l2-2M16.4 7.6l2-2"
                    stroke="white" strokeWidth="1.6" strokeLinecap="round" opacity=".6"/>
                </svg>
              </div>
              {/* 3 orbiting dots — green when active, gray when off */}
              {[0,1,2].map(i => (
                <div key={i} style={{
                  position:'absolute', top:'50%', left:'50%',
                  width:10, height:10, marginTop:-5, marginLeft:-5,
                  borderRadius:'50%',
                  background: ds.PM==='2' ? '#34C759' : '#c7c7cc',
                  boxShadow: ds.PM==='2' ? '0 0 8px rgba(52,199,89,.8)' : 'none',
                  transformOrigin:'0 0',
                  animation:`dotOrbit 2.4s linear ${i*0.8}s infinite`,
                }}/>
              ))}
            </div>

            <div style={{
              fontSize:22, fontWeight:900, letterSpacing:-.4,
              color: ds.PM==='2' ? '#34C759' : '#1c1c1e',
              marginBottom:6,
            }}>
              {ds.PM==='2' ? 'Active' : 'Standby'}
            </div>
            <div style={{
              fontSize:12, color: ds.PM==='2' ? 'rgba(52,199,89,.7)' : '#636366',
              fontWeight:500,
            }}>
              {ds.PM==='2' ? '● Purifying now' : '○ Pump off'}
            </div>
            <style>{`
              @keyframes pumpRing {
                0%   { transform:scale(.4); opacity:.5; }
                100% { transform:scale(1.6); opacity:0; }
              }
              @keyframes pumpSpin {
                from { transform:rotate(0deg); }
                to   { transform:rotate(360deg); }
              }
              @keyframes dotOrbit {
                from { transform:rotate(0deg) translateX(52px); }
                to   { transform:rotate(360deg) translateX(52px); }
              }
            `}</style>
          </div>

          {/* RIGHT — Tank Level animated card — always full colour blue */}
          {(() => {
            const tk = ds.TK;
            const pct = { '1':12, '2':50, '3':92, '4':32 }[tk] ?? 50;
            const col = '#007AFF';  // always blue
            const lbl = tankLevel(tk);
            return (
              <div style={{
                background: 'linear-gradient(160deg,#f0f8ff,#fff)',
                borderRadius:20, padding:'28px 20px',
                boxShadow:'0 4px 24px rgba(0,0,0,.08)',
                border: '1.5px solid rgba(0,122,255,.25)',
                display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                minHeight:240, position:'relative', overflow:'hidden',
              }}>
                <div style={{ position:'absolute',top:0,left:0,right:0,height:4,
                  background:'linear-gradient(90deg,#007AFF,#0056d3)',
                  borderRadius:'20px 20px 0 0' }}/>
                <div style={{ fontSize:10, color:'#8E8E93', fontWeight:700, letterSpacing:1,
                  marginBottom:20, textAlign:'center' }}>TANK LEVEL</div>

                {/* Large tank SVG — always blue, fast wave */}
                <div style={{ position:'relative', width:100, height:130, marginBottom:20 }}>
                  <svg width="100" height="130" viewBox="0 0 100 130" fill="none" overflow="visible">
                    <defs>
                      <clipPath id="tankBodyClip">
                        <rect x="8" y="14" width="84" height="108" rx="12"/>
                      </clipPath>
                      <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#007AFF" stopOpacity="0.7"/>
                        <stop offset="100%" stopColor="#0056d3" stopOpacity="0.9"/>
                      </linearGradient>
                    </defs>
                    {/* No tank cap */}
                    {/* Tank body outline */}
                    <rect x="8" y="14" width="84" height="108" rx="12"
                      stroke="#007AFF" strokeWidth="3" fill="rgba(0,122,255,.07)"/>
                    {/* Water fill */}
                    <g clipPath="url(#tankBodyClip)">
                      {/* Water body */}
                      <rect
                        x="8"
                        y={14 + 108*(1-pct/100)}
                        width="84"
                        height={108*(pct/100)}
                        fill="url(#waterGrad)"
                        style={{ transition:'y 1.4s cubic-bezier(.4,0,.2,1), height 1.4s cubic-bezier(.4,0,.2,1)' }}
                      />
                      {/* Fast animated wave */}
                      <path
                        d={`M-4,${14+108*(1-pct/100)} Q25,${14+108*(1-pct/100)-12} 50,${14+108*(1-pct/100)} Q75,${14+108*(1-pct/100)+12} 104,${14+108*(1-pct/100)} L104,130 L-4,130 Z`}
                        fill="#007AFF" opacity=".5"
                        style={{ animation:'tankWaveFast 1.8s ease-in-out infinite' }}
                      />
                      {/* Second wave layer for depth */}
                      <path
                        d={`M-4,${14+108*(1-pct/100)+4} Q25,${14+108*(1-pct/100)-8} 50,${14+108*(1-pct/100)+4} Q75,${14+108*(1-pct/100)+14} 104,${14+108*(1-pct/100)+4} L104,130 L-4,130 Z`}
                        fill="#0056d3" opacity=".3"
                        style={{ animation:'tankWaveFast 1.8s ease-in-out 0.9s infinite' }}
                      />
                      {/* Shine */}
                      <rect x="18" y={14+108*(1-pct/100)+4} width="16" height={Math.max(0,108*(pct/100)-8)} rx="8"
                        fill="white" opacity=".2"/>
                    </g>
                    {/* % label — white so it's readable against blue water */}
                    <text x="50" y="80" textAnchor="middle"
                      fontSize="20" fontWeight="900" fill="white"
                      fontFamily="-apple-system,BlinkMacSystemFont,sans-serif"
                      style={{ filter:'drop-shadow(0 1px 3px rgba(0,0,0,.4))' }}>
                      {pct}%
                    </text>
                    {/* Tick marks */}
                    {[25,50,75].map(t => (
                      <line key={t} x1="88" y1={14+108*(1-t/100)} x2="96" y2={14+108*(1-t/100)}
                        stroke="#007AFF" strokeWidth="1.5" opacity=".4"/>
                    ))}
                  </svg>
                  <style>{`
                    @keyframes tankWaveFast {
                      0%,100% { transform:translateX(0); }
                      50%      { transform:translateX(-16px); }
                    }
                  `}</style>
                </div>

                <div style={{
                  fontSize:22, fontWeight:900, letterSpacing:-.4,
                  color: '#007AFF', marginBottom:6,
                }}>{lbl ?? ''}</div>
                <div style={{ fontSize:12, color:'rgba(0,122,255,.7)', fontWeight:500 }}>
                  Water tank fill status
                </div>
              </div>
            );
          })()}
        </div>

        {/* ══════════════════════════════════════════
            SECTION 3 — System Info
        ══════════════════════════════════════════ */}
        {/* ══ Filter Health ══ */}
        <SectionHead Icon={Icons.Filter} color="#007AFF"
          title="Filter Health" sub="Each filter remaining life percentage"/>

        <style>{`
          @keyframes filterDrop {
            0%,100% { transform:translateY(0) scaleY(1); opacity:1; }
            40%     { transform:translateY(6px) scaleY(1.15); opacity:0.8; }
            70%     { transform:translateY(8px) scaleY(0.5) scaleX(1.4); opacity:0.4; }
            85%     { transform:translateY(8px) scaleY(0); opacity:0; }
          }
          @keyframes filterFlow {
            0%   { stroke-dashoffset:100; opacity:0.3; }
            50%  { stroke-dashoffset:0;   opacity:1; }
            100% { stroke-dashoffset:-100; opacity:0.3; }
          }
          @keyframes filterSpin {
            from { transform:rotate(0deg); transform-origin:center; }
            to   { transform:rotate(360deg); transform-origin:center; }
          }
          @keyframes filterPulse {
            0%,100% { opacity:1; transform:scale(1); }
            50%     { opacity:0.5; transform:scale(0.85); }
          }
          @keyframes filterBubble {
            0%   { transform:translateY(0) scale(1); opacity:0.8; }
            100% { transform:translateY(-18px) scale(0.4); opacity:0; }
          }
          @keyframes filterShimmer {
            0%   { left:-60%; }
            100% { left:140%; }
          }
          @keyframes filterBar {
            from { width:0%; }
            to   { width:var(--fw); }
          }
          .filter-card-hover:hover {
            transform:translateY(-3px);
            box-shadow:0 8px 28px rgba(0,0,0,.12) !important;
          }
        `}</style>

        {(() => {
          const filterDefs = [
            {
              name:'Carbon', color:'#6B7280', accent:'#4B5563',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  {/* Layered horizontal bars representing carbon layers */}
                  {[3,9,15,21].map((y,i) => (
                    <rect key={i} x="3" y={y} width={28-i*2} height="3.5" rx="1.75"
                      fill={color} opacity={1 - i*0.18}
                      style={{ animation:`filterPulse 2s ease-in-out ${i*0.35}s infinite` }}/>
                  ))}
                  {/* Single drop falling through */}
                  <ellipse cx="18" cy="30" rx="2.5" ry="3.5" fill={color} opacity="0.8"
                    style={{ animation:'filterDrop 2.5s ease-in-out 0.6s infinite' }}/>
                </svg>
              ),
            },
            {
              name:'Sediment', color:'#A78BFA', accent:'#7C3AED',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  {/* Two concentric rings */}
                  <circle cx="18" cy="18" r="13" stroke={color} strokeWidth="1.2" opacity="0.2"/>
                  <circle cx="18" cy="18" r="8"  stroke={color} strokeWidth="1.2" opacity="0.4"/>
                  {/* 4 orbiting sediment particles */}
                  {[0,1,2,3].map(i => (
                    <circle key={i} cx={18 + 13*Math.cos(i*Math.PI/2)} cy={18 + 13*Math.sin(i*Math.PI/2)}
                      r="2.2" fill={color} opacity="0.85"
                      style={{ animation:`filterSpin 3.5s linear ${i*0.875}s infinite`,
                        transformOrigin:'18px 18px' }}/>
                  ))}
                  <circle cx="18" cy="18" r="2.8" fill={color}/>
                </svg>
              ),
            },
            {
              name:'RO', color:'#0ea5e9', accent:'#0369a1',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  {/* Membrane rectangle */}
                  <rect x="5" y="7" width="26" height="20" rx="5"
                    stroke={color} strokeWidth="1.4" fill={color} fillOpacity="0.07"/>
                  {/* 3 horizontal flow dashes moving right */}
                  {[11,17,23].map((y,i) => (
                    <line key={i} x1="5" y1={y} x2="31" y2={y}
                      stroke={color} strokeWidth="1.6" strokeDasharray="4 3"
                      style={{ animation:`filterFlow 1.8s linear ${i*0.55}s infinite` }}/>
                  ))}
                  {/* 3 bubbles rising below */}
                  {[10,18,26].map((x,i) => (
                    <circle key={i} cx={x} cy="32" r="1.4" fill={color} opacity="0.7"
                      style={{ animation:`filterBubble 2.2s ease-out ${i*0.65}s infinite` }}/>
                  ))}
                </svg>
              ),
            },
            {
              name:'Alkaline', color:'#10b981', accent:'#047857',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  {/* Conical flask */}
                  <path d="M13 4v10L5 28h26L23 14V4" stroke={color} strokeWidth="1.4"
                    strokeLinejoin="round" fill={color} fillOpacity="0.07"/>
                  <line x1="12" y1="4" x2="24" y2="4" stroke={color} strokeWidth="2" strokeLinecap="round"/>
                  {/* Bubbles rising in flask */}
                  {[12,18,24].map((x,i) => (
                    <circle key={i} cx={x} cy={23-i*2} r="1.4" fill={color} opacity="0.75"
                      style={{ animation:`filterBubble 2s ease-out ${i*0.55}s infinite` }}/>
                  ))}
                </svg>
              ),
            },
            {
              name:'Mineral', color:'#f59e0b', accent:'#b45309',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  {/* Hexagon crystal */}
                  <polygon points="18,3 32,11 32,25 18,33 4,25 4,11"
                    stroke={color} strokeWidth="1.4" fill={color} fillOpacity="0.07"/>
                  <polygon points="18,9 27,14 27,22 18,27 9,22 9,14"
                    fill={color} fillOpacity="0.15"
                    style={{ animation:`filterPulse 2.2s ease-in-out infinite` }}/>
                  <circle cx="18" cy="18" r="3.5" fill={color}
                    style={{ animation:`filterPulse 1.5s ease-in-out 0.4s infinite` }}/>
                </svg>
              ),
            },
            {
              name:'Copper', color:'#b45309', accent:'#92400e',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  {/* Outer spiral arc */}
                  <path d="M18 4 C28 4 32 10 32 18 C32 26 26 32 18 32 C10 32 4 26 4 18 C4 10 8 6 14 5"
                    stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"
                    style={{ animation:`filterSpin 4s linear infinite`, transformOrigin:'18px 18px' }}/>
                  {/* Inner arc */}
                  <path d="M18 10 C24 10 26 14 26 18 C26 22 22 26 18 26"
                    stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.5"
                    style={{ animation:`filterSpin 4s linear 1s infinite`, transformOrigin:'18px 18px' }}/>
                  <circle cx="18" cy="18" r="3" fill={color}/>
                </svg>
              ),
            },
          ];
          const vals = ds.F ? ds.F.split('-').map(v => parseInt(v,10)) : [];
          // vals[0]=CARBON, vals[1]=SEDIMENT, vals[2]=RO, vals[3]=ALKALINE, vals[4]=MINERAL, vals[5]=COPPER
          // First 3 filters shown individually; last 3 grouped as "3in1" using RO value.
          const roVal = vals[2] ?? null; // RO value drives the 3in1 card

          const singleFilters = [
            {
              name:'Carbon', color:'#6366f1', accent:'#4338ca',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <rect x="10" y="4" width="16" height="28" rx="4" stroke={color} strokeWidth="1.4" fill={color} fillOpacity="0.07"/>
                  {[9,14,19,24].map((y,i) => (
                    <line key={i} x1="13" y1={y} x2="23" y2={y} stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity={0.4+i*0.15}/>
                  ))}
                  {[13,18,23].map((x,i) => (
                    <circle key={i} cx={x} cy="32" r="1.4" fill={color} opacity="0.7"
                      style={{ animation:`filterFlow 1.8s linear ${i*0.55}s infinite` }}/>
                  ))}
                  {[10,18,26].map((x,i) => (
                    <circle key={i} cx={x} cy="32" r="1.4" fill={color} opacity="0.7"
                      style={{ animation:`filterBubble 2.2s ease-out ${i*0.65}s infinite` }}/>
                  ))}
                </svg>
              ),
            },
            {
              name:'Sediment', color:'#f97316', accent:'#c2410c',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <rect x="8" y="3" width="20" height="30" rx="5" stroke={color} strokeWidth="1.4" fill={color} fillOpacity="0.07"/>
                  {[8,13,19,25].map((y,i) => (
                    <path key={i} d={`M11 ${y} Q18 ${y-2} 25 ${y}`} stroke={color} strokeWidth="1" opacity={0.35+i*0.15} fill="none"/>
                  ))}
                  <circle cx="18" cy="29" r="3" fill={color} opacity="0.3"
                    style={{ animation:`filterPulse 2s ease-in-out infinite` }}/>
                </svg>
              ),
            },
            {
              name:'RO', color:'#0ea5e9', accent:'#0284c7',
              Anim: ({color}) => (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="13" stroke={color} strokeWidth="1.4" fill={color} fillOpacity="0.07"
                    style={{ animation:`filterSpin 8s linear infinite`, transformOrigin:'18px 18px' }}/>
                  <circle cx="18" cy="18" r="8" stroke={color} strokeWidth="1" opacity="0.4"/>
                  <circle cx="18" cy="18" r="3" fill={color}
                    style={{ animation:`filterPulse 1.8s ease-in-out infinite` }}/>
                  {[0,60,120,180,240,300].map((deg,i) => {
                    const rad = (deg * Math.PI) / 180;
                    const x = 18 + 10 * Math.cos(rad);
                    const y = 18 + 10 * Math.sin(rad);
                    return <circle key={i} cx={x} cy={y} r="1.2" fill={color} opacity="0.5"/>;
                  })}
                </svg>
              ),
            },
          ];

          return (
            <div style={{ display:'flex', gap:12, marginBottom:8 }}>
              {/* ── Individual filter cards: Carbon, Sediment, RO ── */}
              {singleFilters.map((f, i) => {
                const pct = vals[i] ?? null;
                const healthColor = pct == null ? f.color
                  : pct > 60 ? f.color
                  : pct > 25 ? '#FF9F0A'
                  : '#FF3B30';
                return (
                  <div key={f.name} className="filter-card-hover" style={{
                    flex:1,
                    background:'#fff', borderRadius:18, padding:'14px 12px 12px',
                    boxShadow:'0 2px 14px rgba(0,0,0,.07)',
                    border:`1.5px solid ${f.color}28`,
                    display:'flex', flexDirection:'column', gap:8,
                    position:'relative', overflow:'hidden',
                    transition:'transform .22s ease, box-shadow .22s ease',
                    cursor:'default',
                    minWidth:0,
                  }}>
                    <div style={{ position:'absolute',top:0,left:0,right:0,height:3,
                      background:`linear-gradient(90deg,${f.color},${f.color}66)`,
                      borderRadius:'18px 18px 0 0' }}/>
                    <div style={{ position:'absolute',top:0,bottom:0,width:'40%',
                      background:'linear-gradient(90deg,transparent,rgba(255,255,255,.5),transparent)',
                      animation:'filterShimmer 3s ease-in-out infinite',
                      pointerEvents:'none' }}/>
                    <div style={{
                      width:42, height:42, borderRadius:12,
                      background:`linear-gradient(135deg,${f.color}18,${f.color}08)`,
                      border:`1px solid ${f.color}22`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                    }}>
                      <f.Anim color={f.color}/>
                    </div>
                    <div style={{ fontSize:9, color:'#8E8E93', fontWeight:700, letterSpacing:.7 }}>
                      {f.name.toUpperCase()}
                    </div>
                    {pct != null ? (
                      <>
                        <div style={{ fontSize:22, fontWeight:900, color:healthColor, letterSpacing:-.5, lineHeight:1 }}>
                          {pct}<span style={{ fontSize:12, fontWeight:600 }}>%</span>
                        </div>
                        <div style={{ background:'rgba(0,0,0,.06)', borderRadius:999, height:5, overflow:'hidden' }}>
                          <div style={{
                            height:'100%', borderRadius:999,
                            background:`linear-gradient(90deg,${healthColor},${healthColor}99)`,
                            width:`${Math.min(pct,100)}%`,
                            boxShadow:`0 0 8px ${healthColor}60`,
                          }}/>
                        </div>
                        <div style={{
                          fontSize:8, fontWeight:800, padding:'2px 6px', borderRadius:20,
                          background: pct>60?`${f.color}12`:pct>25?'rgba(255,159,10,.12)':'rgba(255,59,48,.1)',
                          color:healthColor, letterSpacing:.5, width:'fit-content',
                        }}>
                          {pct>60?'GOOD':pct>25?'LOW':'CRITICAL'}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize:13, color:'#C7C7CC' }}>—</div>
                    )}
                  </div>
                );
              })}

              {/* ── 3in1 combined card: Alkaline + Mineral + Copper (uses RO value) ── */}
              {(() => {
                const pct = roVal;
                const c3 = '#8b5cf6'; // purple for 3in1
                const healthColor = pct == null ? c3
                  : pct > 60 ? c3
                  : pct > 25 ? '#FF9F0A'
                  : '#FF3B30';
                return (
                  <div className="filter-card-hover" style={{
                    flex:1,
                    background:'linear-gradient(135deg,#f5f3ff,#ede9fe)',
                    borderRadius:18, padding:'14px 12px 12px',
                    boxShadow:'0 2px 14px rgba(139,92,246,.12)',
                    border:`1.5px solid ${c3}30`,
                    display:'flex', flexDirection:'column', gap:8,
                    position:'relative', overflow:'hidden',
                    transition:'transform .22s ease, box-shadow .22s ease',
                    cursor:'default',
                    minWidth:0,
                  }}>
                    {/* coloured top bar */}
                    <div style={{ position:'absolute',top:0,left:0,right:0,height:3,
                      background:`linear-gradient(90deg,${c3},#a78bfa,#c4b5fd)`,
                      borderRadius:'18px 18px 0 0' }}/>
                    {/* shimmer */}
                    <div style={{ position:'absolute',top:0,bottom:0,width:'40%',
                      background:'linear-gradient(90deg,transparent,rgba(255,255,255,.5),transparent)',
                      animation:'filterShimmer 3s ease-in-out infinite',
                      pointerEvents:'none' }}/>

                    {/* 3-icon cluster */}
                    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                      {['🌿','💎','🔶'].map((em,i) => (
                        <div key={i} style={{
                          width:26, height:26, borderRadius:8,
                          background:'rgba(139,92,246,.12)',
                          border:'1px solid rgba(139,92,246,.2)',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:13,
                        }}>{em}</div>
                      ))}
                    </div>

                    {/* Label */}
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <div style={{ fontSize:9, color:c3, fontWeight:800, letterSpacing:.7 }}>3IN1</div>
                      <div style={{
                        fontSize:7, color:'#8E8E93', fontWeight:600, letterSpacing:.4,
                        lineHeight:1.2,
                      }}>ALK · MIN · COP</div>
                    </div>

                    {pct != null ? (
                      <>
                        <div style={{ fontSize:22, fontWeight:900, color:healthColor, letterSpacing:-.5, lineHeight:1 }}>
                          {pct}<span style={{ fontSize:12, fontWeight:600 }}>%</span>
                        </div>
                        <div style={{ background:'rgba(139,92,246,.15)', borderRadius:999, height:5, overflow:'hidden' }}>
                          <div style={{
                            height:'100%', borderRadius:999,
                            background:`linear-gradient(90deg,${healthColor},${healthColor}99)`,
                            width:`${Math.min(pct,100)}%`,
                            boxShadow:`0 0 8px ${healthColor}60`,
                          }}/>
                        </div>
                        <div style={{
                          fontSize:8, fontWeight:800, padding:'2px 6px', borderRadius:20,
                          background: pct>60?'rgba(139,92,246,.1)':pct>25?'rgba(255,159,10,.12)':'rgba(255,59,48,.1)',
                          color:healthColor, letterSpacing:.5, width:'fit-content',
                        }}>
                          {pct>60?'GOOD':pct>25?'LOW':'CRITICAL'}
                        </div>
                        {/* sub-labels */}
                        <div style={{ display:'flex', flexDirection:'column', gap:2, marginTop:2 }}>
                          {[{name:'Alkaline'},{name:'Mineral'},{name:'Copper'}].map(sub => (
                            <div key={sub.name} style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                              <span style={{ fontSize:8, color:'#8E8E93', fontWeight:600 }}>{sub.name}</span>
                              <span style={{ fontSize:8, fontWeight:700, color:healthColor }}>{pct}%</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize:13, color:'#C7C7CC' }}>—</div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* ══ System Info ══ */}
        <SectionHead Icon={Icons.Chip} color="#8E8E93"
          title="System Information" sub="Firmware, hardware & diagnostics"/>

        <style>{`
          @keyframes sysRowIn { from{opacity:0;transform:translateX(-10px)} to{opacity:1;transform:translateX(0)} }
          @keyframes btnPulse { 0%,100%{box-shadow:0 4px 16px var(--bs)} 50%{box-shadow:0 8px 28px var(--bs)} }
          @keyframes btnShine {
            0%   { left:-80%; }
            100% { left:120%; }
          }
          .sys-row {
            display:flex; justify-content:space-between; align-items:center;
            padding:12px 16px; border-radius:12px; margin-bottom:6px;
            background:rgba(0,0,0,.025); transition:background .15s;
          }
          .sys-row:hover { background:rgba(0,122,255,.05); }
          .action-btn {
            position:relative; overflow:hidden;
            transition:transform .15s ease, box-shadow .15s ease;
          }
          .action-btn:hover  { transform:translateY(-2px); }
          .action-btn:active { transform:scale(.96); }
          .action-btn::after {
            content:'';
            position:absolute; top:0; left:-80%; width:60%; height:100%;
            background:linear-gradient(90deg,transparent,rgba(255,255,255,.25),transparent);
            animation:btnShine 2.5s ease-in-out infinite;
          }
        `}</style>

        {/* Glassmorphism card with coloured stat chips */}
        <div style={{
          background:'linear-gradient(135deg,#f8faff 0%,#ffffff 100%)',
          borderRadius:22, padding:'20px',
          boxShadow:'0 4px 24px rgba(0,0,0,.08)',
          border:'1px solid rgba(0,0,0,.06)', marginBottom:8,
        }}>
          {/* Top row — 3 highlight chips in Havells red */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
            {[
              { label:'FIRMWARE',  value:ds.FW  || '—', color:'#DC2626', bg:'rgba(220,38,38,.08)'  },
              { label:'MCU',       value:ds.MCU || '—', color:'#DC2626', bg:'rgba(220,38,38,.06)' },
              { label:'VALIDITY',  value:ds.V && ds.V!=='000' ? ds.V : '—', color:'#DC2626', bg:'rgba(220,38,38,.05)' },
            ].map((chip,i) => (
              <div key={i} style={{
                background:chip.bg,
                borderRadius:14, padding:'12px 14px',
                border:`1px solid rgba(220,38,38,.18)`,
                animation:`sysRowIn .35s ease ${i*.08}s both`,
                position:'relative', overflow:'hidden',
              }}>
                {/* Red top bar */}
                <div style={{ position:'absolute',top:0,left:0,right:0,height:3,
                  background:'linear-gradient(90deg,#DC2626,#EF4444)',
                  borderRadius:'14px 14px 0 0' }}/>
                <div style={{ fontSize:9, color:'#DC2626', fontWeight:800, letterSpacing:.8, marginBottom:5 }}>
                  {chip.label}
                </div>
                <div style={{ fontSize:15, fontWeight:800, color:'#1c1c1e', fontFamily:'"SF Mono",monospace',
                  letterSpacing:-.3, wordBreak:'break-all' }}>
                  {chip.value}
                </div>
              </div>
            ))}
          </div>

          {/* Detail rows */}
          <div className="ds-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
            <div>
              {[
                { label:'Hardware',       value:ds.HW,  mono:true  },
                { label:'Last Request',   value:ds.T,   mono:false },
                { label:'Remaining Litres', value:ds.LR, mono:false },
              ].map((r,i) => (
                <div key={i} className="sys-row" style={{ animationDelay:`${i*.06}s` }}>
                  <span style={{ fontSize:12, color:'#8E8E93', fontWeight:500 }}>{r.label}</span>
                  <span style={{ fontSize:13, color:'#1c1c1e', fontWeight:700,
                    fontFamily: r.mono ? '"SF Mono",monospace' : 'inherit' }}>
                    {r.value ?? '—'}
                  </span>
                </div>
              ))}
            </div>
            <div>
              {[
                { label:'Total Litres',  value:ds.TL ? `${ds.TL} L` : null },
                { label:'TAM Alert',     value:ds.TAM==='0'?'None':ds.TAM,
                  pill: ds.TAM && ds.TAM!=='0' ? (
                    <span style={{ fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,
                      background:'rgba(255,59,48,.1)',color:'#FF3B30' }}>{ds.TAM}</span>
                  ) : null },
                { label:'OTA Update',    value:ds.OTA==='0'?'Up to date':ds.OTA,
                  pill: ds.OTA==='0' ? (
                    <span style={{ fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,
                      background:'rgba(52,199,89,.1)',color:'#34C759' }}>✓ Latest</span>
                  ) : null },
              ].map((r,i) => (
                <div key={i} className="sys-row" style={{ animationDelay:`${i*.06+.18}s` }}>
                  <span style={{ fontSize:12, color:'#8E8E93', fontWeight:500 }}>{r.label}</span>
                  {r.pill || <span style={{ fontSize:13, color:'#1c1c1e', fontWeight:700 }}>{r.value ?? '—'}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>



      </>)}

      {/* ══ Sticky bottom action bar ══ */}
      <div style={{
        position:'sticky', bottom:0, left:0, right:0,
        background:'rgba(255,255,255,0.92)',
        backdropFilter:'blur(20px)',
        borderTop:'1px solid rgba(0,0,0,.08)',
        padding:'12px 16px',
        display:'flex', gap:12,
        zIndex:100,
        marginTop:12,
      }}>
        {/* Group of 4 main action buttons — Diagnostics dropdown centers under THIS group */}
        <div style={{ position:'relative', display:'flex', gap:12, flex:1 }}>
        {/* Diagnostics */}
        <button onClick={() => setShowDiag(true)} className="action-btn" style={{
          flex:1, padding:'14px', borderRadius:16, border:'none',
          background:'linear-gradient(135deg,#EF4444,#DC2626,#B91C1C)',
          color:'#fff', fontWeight:800, fontSize:14,
          cursor:'pointer', fontFamily:'inherit',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          boxShadow:'0 4px 20px rgba(220,38,38,.45)',
          '--bs':'rgba(220,38,38,.45)',
          animation:'btnPulse 3s ease-in-out infinite',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Health Check
        </button>

        {/* Send Request */}
        <button onClick={() => setShowRequest(true)} className="action-btn" style={{
          flex:1, padding:'14px', borderRadius:16, border:'none',
          background:'linear-gradient(135deg,#EF4444,#DC2626,#B91C1C)',
          color:'#fff', fontWeight:800, fontSize:14,
          cursor:'pointer', fontFamily:'inherit',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          boxShadow:'0 4px 18px rgba(220,38,38,.4)',
          '--bs':'rgba(220,38,38,.4)',
          animation:'btnPulse 3s ease-in-out 0.5s infinite',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Add on Request
        </button>

        {/* Recharge */}
        <button onClick={() => setShowRecharge(true)} className="action-btn" style={{
          flex:1, padding:'14px', borderRadius:16, border:'none',
          background:'linear-gradient(135deg,#EF4444,#DC2626,#B91C1C)',
          color:'#fff', fontWeight:800, fontSize:14,
          cursor:'pointer', fontFamily:'inherit',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          boxShadow:'0 4px 20px rgba(220,38,38,.45)',
          '--bs':'rgba(220,38,38,.45)',
          animation:'btnPulse 3s ease-in-out 1s infinite',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 14H11v-4H8l4-7 4 7h-3v4z" fill="white"/>
          </svg>
          Recharge
        </button>

        {/* Factory Reset */}
        <button onClick={() => setShowFactoryReset(true)} className="action-btn" style={{
          flex:1, padding:'14px', borderRadius:16, border:'none',
          background:'linear-gradient(135deg,#374151,#1F2937,#111827)',
          color:'#fff', fontWeight:800, fontSize:14,
          cursor:'pointer', fontFamily:'inherit',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          boxShadow:'0 4px 18px rgba(17,24,39,.4)',
          '--bs':'rgba(17,24,39,.4)',
          animation:'btnPulse 3s ease-in-out 1.5s infinite',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M3 12a9 9 0 1 0 9-9" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <path d="M3 4v5h5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Factory Reset
        </button>

        {/* Error Stack */}
        <button onClick={() => setShowErrorStack(true)} className="action-btn" style={{
          flex:1, padding:'14px', borderRadius:16, border:'none',
          background:'linear-gradient(135deg,#374151,#1F2937,#111827)',
          color:'#fff', fontWeight:800, fontSize:14,
          cursor:'pointer', fontFamily:'inherit',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          boxShadow:'0 4px 18px rgba(17,24,39,.4)',
          '--bs':'rgba(17,24,39,.4)',
          animation:'btnPulse 3s ease-in-out 2s infinite',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Error Stack
        </button>
        {showDiag && <DiagnosticsModal device={device} mac={mac} connected={connected} publish={publish} subscribe={subscribe} onClose={() => setShowDiag(false)} k2={activeK2}/>}
        {showErrorStack && <ErrorStackModal device={device} mac={mac} connected={connected} publish={publish} subscribe={subscribe} onClose={() => setShowErrorStack(false)} k2={activeK2}/>}
        </div>

        {/* Tamper alert button — stays outside the 4-button group, unaffected by dropdown centering */}
        <button onClick={()=>{
          setTamUnacked(getTamHistory(mac).filter(h=>!h.acknowledgedAt).length);
          setShowTamHistory(true);
        }} className="action-btn" style={{
          position:'relative',
          width:52, padding:'14px 0', borderRadius:16, border:'none',
          background: tamUnacked>0
            ? 'linear-gradient(135deg,#FF453A,#FF3B30,#CC0000)'
            : 'linear-gradient(135deg,#f2f2f7,#e5e5ea)',
          color: tamUnacked>0?'#fff':'#8E8E93', fontWeight:700, fontSize:11,
          cursor:'pointer', fontFamily:'inherit', flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow: tamUnacked>0 ? '0 4px 16px rgba(255,59,48,.45)' : '0 2px 8px rgba(0,0,0,.1)',
          '--bs': tamUnacked>0 ? 'rgba(255,59,48,.45)' : 'rgba(0,0,0,.1)',
          animation: tamUnacked>0 ? 'btnPulse 2s ease-in-out infinite' : 'none',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 19h20L12 2Z"
              fill={tamUnacked>0?'white':'#8E8E93'} fillOpacity=".3"
              stroke={tamUnacked>0?'white':'#8E8E93'}
              strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M12 9v5" stroke={tamUnacked>0?'white':'#8E8E93'} strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="17" r="1" fill={tamUnacked>0?'white':'#8E8E93'}/>
          </svg>
          {tamUnacked>0 && (
            <span style={{
              position:'absolute', top:6, right:6,
              width:15, height:15, borderRadius:'50%',
              background:'#fff', color:'#FF3B30',
              fontSize:8, fontWeight:800,
              display:'flex', alignItems:'center', justifyContent:'center',
              border:'1.5px solid #FF3B30',
              boxShadow:'0 1px 4px rgba(0,0,0,.2)',
            }}>{tamUnacked}</span>
          )}
        </button>
      </div>

      {/* ══ Sheets ══ */}
      {showFactoryReset && <FactoryResetModal device={device} mac={mac} connected={connected} publish={publish} subscribe={subscribe} onClose={() => setShowFactoryReset(false)} k2={activeK2}/>}
      {showRecharge    && <RechargeSheet device={device} mac={mac} publish={publish} subscribe={subscribe} onClose={() => setShowRecharge(false)} k2={activeK2}/>}
      {showRequest     && <SendRequestSheet device={device} mac={mac} connected={connected} publish={publish} subscribe={subscribe} onClose={() => setShowRequest(false)} k2={activeK2}/>}
      {tamAlert        && <TamperToast tamCode={tamAlert} device={device} mac={mac}
        onAck={() => { sendTamAck(device.macid, publish, getK2); setTamAlert(null); }}
        onClose={() => setTamAlert(null)}/>}
      {showTamHistory  && <TamperHistoryPanel mac={mac} deviceName={device.friendlyname}
        device={device} publish={publish} getK2={getK2} onClose={() => {
        setTamUnacked(getTamHistory(mac).filter(h=>!h.acknowledgedAt).length);
        setShowTamHistory(false);
      }}/>}
    </div>
  );
}
